<template>
  <div class="character-sheet-wrapper py-4">
    <div class="container">
      <div v-if="!user" class="alert alert-warning shadow-sm">
        <i class="bi bi-lock-fill me-2"></i>
        Faça login para criar e gerenciar suas fichas de personagem.
      </div>

      <div v-else>
        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4 gap-3">
          <div>
            <h2 class="text-uppercase mb-1">
              <i class="bi bi-file-earmark-person me-2"></i>
              Fichas de Personagem
            </h2>
            <p class="text-muted mb-0">
              Plano {{ accountTypeLabel }} — {{ sheetLimit }} ficha<span v-if="sheetLimit > 1">s</span> disponíveis.
              <span v-if="remainingSlots > 0">Você ainda pode criar {{ remainingSlots }} ficha<span v-if="remainingSlots > 1">s</span>.</span>
              <span v-else class="text-warning">Limite atingido. Edite ou remova uma ficha existente para criar novas.</span>
            </p>
          </div>
          <div class="text-lg-end">
            <button class="btn btn-outline-light" type="button" @click="resetForm" :disabled="saving || isAtLimit">
              <i class="bi bi-file-plus me-2"></i>
              Nova ficha
            </button>
          </div>
        </div>

        <transition name="fade">
          <div v-if="error" class="alert alert-danger">{{ error }}</div>
        </transition>
        <transition name="fade">
          <div v-if="success" class="alert alert-success">{{ success }}</div>
        </transition>

        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-light" role="status"></div>
          <p class="mt-3 mb-0">Carregando fichas de personagem...</p>
        </div>

        <div v-else class="row g-4">
          <div class="col-lg-4">
            <div class="card sheet-card h-100">
              <div class="card-body">
                <h5 class="text-uppercase mb-3">Minhas fichas</h5>

                <div v-if="!sheets.length" class="text-muted small">
                  Você ainda não possui fichas cadastradas. Use o formulário ao lado para criar a primeira.
                </div>

                <ul v-else class="list-group list-group-flush">
                  <li
                    v-for="sheet in sheets"
                    :key="sheet.id"
                    class="list-group-item bg-transparent text-light d-flex justify-content-between align-items-start"
                  >
                    <div>
                      <h6 class="mb-1 d-flex align-items-center gap-2">
                        {{ sheet.name }}
                        <span
                          class="badge"
                          :class="sheet.status === 'final' ? 'bg-success text-dark' : 'bg-secondary text-light'"
                        >
                          {{ sheet.status === 'final' ? 'Definitiva' : 'Rascunho' }}
                        </span>
                      </h6>
                      <small class="text-muted">
                        {{ sheet.clan || 'Sem clã definido' }} — Atualizado em {{ formatDate(sheet.updatedAt || sheet.createdAt) }}
                      </small>
                    </div>
                    <div class="btn-group btn-group-sm">
                      <button class="btn btn-outline-light" type="button" @click="selectSheet(sheet)">
                        <i class="bi" :class="selectedSheetId === sheet.id ? 'bi-pencil-fill' : 'bi-pencil'" aria-hidden="true"></i>
                        <span class="visually-hidden">Editar</span>
                      </button>
                      <button class="btn btn-outline-danger" type="button" @click="confirmDelete(sheet)" :disabled="saving">
                        <i class="bi bi-trash" aria-hidden="true"></i>
                        <span class="visually-hidden">Excluir</span>
                      </button>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="col-lg-8">
            <div class="card sheet-card">
              <div class="card-body">
                <h5 class="text-uppercase mb-3">
                  {{ selectedSheetId ? 'Editar ficha' : 'Criar nova ficha' }}
                </h5>
                <form @submit.prevent="submit">
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">Nome do Personagem *</label>
                      <input type="text" class="form-control" v-model="form.name" required>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Conceito</label>
                      <input type="text" class="form-control" v-model="form.concept">
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Crônica</label>
                      <input type="text" class="form-control" v-model="form.chronicle">
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Seita *</label>
                      <select class="form-select" v-model="form.sect" required :disabled="isCreationLocked">
                        <option value="" disabled>Selecione uma seita</option>
                        <option v-for="sect in availableSects" :key="sect.id" :value="sect.id">{{ sect.name }}</option>
                      </select>
                    </div>
                    <div class="col-md-4">
                      <label class="form-label">Clã *</label>
                      <select class="form-select" v-model="form.clan" required :disabled="!form.sect || isCreationLocked">
                        <option value="" disabled>Selecione um clã</option>
                        <option v-for="clan in availableClans" :key="clan.id" :value="clan.id">{{ clan.name }}</option>
                      </select>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Status da ficha *</label>
                      <select class="form-select" v-model="form.status" :disabled="originalStatus === 'final'" required>
                        <option v-for="option in statusOptions" :key="option.value" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <small class="text-muted d-block mt-1">
                        Somente fichas definitivas podem participar de partidas de RPG.
                      </small>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Geração *</label>
                      <select
                        class="form-select"
                        v-model.number="form.generation"
                        @change="handleGenerationChange"
                        :disabled="isCreationLocked || !canEditGeneration"
                      >
                        <option
                          v-for="option in generationOptions"
                          :key="option.value"
                          :value="option.value"
                        >
                          {{ option.label }}
                        </option>
                      </select>
                      <small class="text-muted d-block mt-1">
                        Sangue Máx.: {{ bloodPoolMax }} | Gasto/Turno: {{ bloodPerTurn }}
                      </small>
                      <small v-if="!canEditGeneration" class="text-muted d-block mt-1">
                        Adicione o antecedente "Geração" para alterar este valor.
                      </small>
                    </div>
                    <div class="col-md-2">
                      <label class="form-label">Linhagem</label>
                      <input type="text" class="form-control" v-model="form.lineage" placeholder="Prole de...">
                    </div>
                    <div class="col-12" v-if="isCreationLocked">
                      <div class="alert alert-info small mb-0">
                        Esta ficha está definitiva. Utilize os pontos de experiência conquistados em jogo para aprimorar
                        atributos, habilidades, disciplinas e antecedentes. XP disponível:
                        {{ availableExperience }} ponto<span v-if="availableExperience !== 1">s</span>.
                      </div>
                    </div>
                  </div>

                  <hr class="my-4">

                  <div class="row g-3 align-items-end mb-2">
                    <div class="col-md-4" v-for="category in attributeSchema" :key="`${category.key}-priority`">
                      <label class="form-label">Prioridade — {{ category.label }}</label>
                      <select
                        class="form-select"
                        :value="attributePriorities[category.key]"
                        @change="setAttributePriority(category.key, $event.target.value)"
                        :disabled="isCreationLocked"
                      >
                        <option v-for="option in attributePriorityOptions" :key="option.value" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <small v-if="!isExperienceMode" class="text-muted d-block mt-1"
                        >Pontos: {{ attributePointsSpent[category.key] }}/{{ attributeLimits[category.key] }}</small
                      >
                      <small v-else class="text-muted d-block mt-1"
                        >Pontos totais: {{ attributePointsSpent[category.key] }} (Limite inicial:
                        {{ attributeLimits[category.key] }})</small
                      >
                    </div>
                  </div>

                  <div class="row g-4">
                    <div class="col-md-6" v-for="category in attributeSchema" :key="category.key">
                      <h6 class="text-uppercase small fw-bold text-muted mb-3">{{ category.label }}</h6>
                      <div v-for="field in category.fields" :key="field.key" class="d-flex align-items-center justify-content-between mb-2">
                        <span>{{ field.label }}</span>
                        <div class="dot-rating" role="group" :aria-label="`Definir ${field.label}`">
                          <button
                            v-for="value in 5"
                            :key="value"
                            type="button"
                            class="dot"
                            :class="{ active: form.attributes[category.key][field.key] >= value }"
                            @click="setAttributeRating(category.key, field.key, value)"
                            :title="`${field.label}: ${value}`"
                            :disabled="isCreationLocked && !isExperienceMode"
                          ></button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="allocationMessages.attributes" class="alert alert-warning mt-2 py-2 px-3 small">
                    {{ allocationMessages.attributes }}
                  </div>

                  <hr class="my-4">

                  <div class="row g-3 align-items-end mb-2">
                    <div class="col-md-4" v-for="group in abilitySchema" :key="`${group.key}-priority`">
                      <label class="form-label">Prioridade — {{ group.label }}</label>
                      <select
                        class="form-select"
                        :value="abilityPriorities[group.key]"
                        @change="setAbilityPriority(group.key, $event.target.value)"
                        :disabled="isCreationLocked"
                      >
                        <option v-for="option in abilityPriorityOptions" :key="option.value" :value="option.value">
                          {{ option.label }}
                        </option>
                      </select>
                      <small v-if="!isExperienceMode" class="text-muted d-block mt-1"
                        >Pontos: {{ abilityPointsSpent[group.key] }}/{{ abilityLimits[group.key] }}</small
                      >
                      <small v-else class="text-muted d-block mt-1"
                        >Pontos totais: {{ abilityPointsSpent[group.key] }} (Limite inicial:
                        {{ abilityLimits[group.key] }})</small
                      >
                    </div>
                  </div>

                  <div class="row g-4">
                    <div class="col-md-4" v-for="group in abilitySchema" :key="group.key">
                      <h6 class="text-uppercase small fw-bold text-muted mb-3">{{ group.label }}</h6>
                      <div v-for="field in group.fields" :key="field.key" class="d-flex align-items-center justify-content-between mb-2">
                        <span>{{ field.label }}</span>
                        <div class="dot-rating" role="group" :aria-label="`Definir ${field.label}`">
                          <button
                            v-for="value in 5"
                            :key="value"
                            type="button"
                            class="dot"
                            :class="{ active: form.abilities[group.key][field.key] >= value }"
                            @click="setAbilityRating(group.key, field.key, value)"
                            :title="`${field.label}: ${value}`"
                            :disabled="isCreationLocked && !isExperienceMode"
                          ></button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div v-if="allocationMessages.abilities" class="alert alert-warning mt-2 py-2 px-3 small">
                    {{ allocationMessages.abilities }}
                  </div>

                  <hr class="my-4">

                  <div class="row g-3 align-items-end">
                    <div class="col-md-6">
                      <div class="d-flex justify-content-between align-items-center">
                        <div>
                          <label class="form-label">Disciplinas do Clã</label>
                          <p class="text-muted small mb-2">
                            Selecione a quantidade de pontos em cada disciplina. Adicione outras se necessário.
                          </p>
                        </div>
                        <span class="badge bg-dark text-warning fw-normal">
                          <template v-if="!isExperienceMode">
                            {{ disciplinePointsSpent }}/{{ disciplineLimit }} pontos
                          </template>
                          <template v-else>
                            {{ disciplinePointsSpent }} pontos totais (limite inicial: {{ disciplineLimit }})
                          </template>
                        </span>
                      </div>
                      <div v-for="(discipline, index) in form.disciplines" :key="discipline.name" class="discipline-row">
                        <div class="d-flex justify-content-between align-items-center">
                          <strong>{{ discipline.name }}</strong>
                          <div class="d-flex align-items-center gap-2">
                            <div class="dot-rating" role="group" :aria-label="`Definir ${discipline.name}`">
                          <button
                            v-for="value in 5"
                            :key="value"
                            type="button"
                            class="dot"
                            :class="{ active: discipline.rating >= value }"
                            @click="setDisciplineRating(index, value)"
                            :title="`${discipline.name}: ${value}`"
                            :disabled="isCreationLocked && !isExperienceMode"
                          ></button>
                        </div>
                        <button
                          v-if="canRemoveDiscipline(discipline.name)"
                          type="button"
                          class="btn btn-sm btn-outline-danger"
                          @click="removeDiscipline(index)"
                          :disabled="isCreationLocked && !isExperienceMode"
                          :title="`Remover ${discipline.name}`"
                        >
                          <i class="bi bi-x"></i>
                        </button>
                      </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Adicionar outra disciplina</label>
                      <div class="input-group">
                        <input
                          type="text"
                          class="form-control"
                          v-model="newDisciplineName"
                          placeholder="Nome da disciplina"
                          :disabled="isCreationLocked && !isExperienceMode"
                        >
                        <button
                          class="btn btn-outline-light"
                          type="button"
                          @click="addDiscipline"
                          :disabled="!newDisciplineName || (isCreationLocked && !isExperienceMode)"
                        >
                          <i class="bi bi-plus-lg"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <div v-if="allocationMessages.disciplines" class="alert alert-warning mt-2 py-2 px-3 small">
                    {{ allocationMessages.disciplines }}
                  </div>

                  <hr class="my-4">

                  <div class="row g-4">
                    <div class="col-md-4" v-for="virtue in virtueSchema" :key="virtue.key">
                      <label class="form-label d-flex justify-content-between align-items-center">
                        <span>{{ virtue.label }}</span>
                        <small class="text-muted">{{ form.virtues[virtue.key] }} pontos</small>
                      </label>
                      <div class="dot-rating" role="group" :aria-label="`Definir ${virtue.label}`">
                        <button
                          v-for="value in 5"
                          :key="value"
                          type="button"
                          class="dot"
                          :class="{ active: form.virtues[virtue.key] >= value }"
                          @click="setVirtueRating(virtue.key, value)"
                          :title="`${virtue.label}: ${value}`"
                          :disabled="isCreationLocked"
                        ></button>
                      </div>
                    </div>
                  </div>

                  <div class="d-flex gap-3 mt-2 small text-muted">
                    <span>Humanidade: {{ humanityValue }}</span>
                    <span>Força de Vontade: {{ willpowerValue }}</span>
                    <span>Pontos de Virtude distribuídos: {{ virtuePointsSpent }}/7</span>
                  </div>

                  <div v-if="allocationMessages.virtues" class="alert alert-warning mt-2 py-2 px-3 small">
                    {{ allocationMessages.virtues }}
                  </div>

                  <hr class="my-4">

                  <div class="row g-4 align-items-stretch">
                    <div class="col-lg-4">
                      <h6 class="text-uppercase small fw-bold text-muted mb-3">Vitalidade</h6>
                      <ul class="health-track list-unstyled mb-0">
                        <li
                          v-for="(level, index) in healthLevels"
                          :key="level.key"
                          class="health-track__item"
                        >
                          <button
                            type="button"
                            class="health-track__box"
                            :class="{ 'health-track__box--filled': (form.health?.current || 0) > index }"
                            @click="setHealthLevel(index)"
                            :aria-pressed="(form.health?.current || 0) > index"
                            :aria-label="`Marcar nível de vitalidade ${level.label}`"
                          ></button>
                          <div class="health-track__label">
                            <span class="health-track__label-title">{{ level.label }}</span>
                            <span v-if="level.penalty" class="health-track__label-penalty">{{ level.penalty }}</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                    <div class="col-lg-8">
                      <div class="row g-4">
                        <div class="col-md-4">
                          <h6 class="text-uppercase small fw-bold text-muted mb-3">Humanidade / Trilha</h6>
                          <div class="track-section">
                            <div class="track-section__label">Valor atual ({{ humanityValue }})</div>
                            <div class="dot-rating dot-rating--readonly" role="presentation">
                              <span
                                v-for="value in humanityMax"
                                :key="`humanity-${value}`"
                                class="dot"
                                :class="{ active: humanityValue >= value }"
                              ></span>
                            </div>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <h6 class="text-uppercase small fw-bold text-muted mb-3">Força de Vontade</h6>
                          <div class="track-section">
                            <div class="track-section__label">Permanente ({{ willpowerMax }})</div>
                            <div class="dot-rating dot-rating--readonly" role="presentation">
                              <span
                                v-for="value in humanityMax"
                                :key="`willpower-permanent-${value}`"
                                class="dot"
                                :class="{ active: willpowerMax >= value }"
                              ></span>
                            </div>
                            <div class="track-section__label mt-3">Temporária ({{ form.willpower?.current || 0 }})</div>
                            <div class="dot-rating" role="group" aria-label="Definir Força de Vontade temporária">
                              <button
                                v-for="value in humanityMax"
                                :key="`willpower-current-${value}`"
                                type="button"
                                class="dot"
                                :class="{
                                  active: (form.willpower?.current || 0) >= value,
                                  'dot--disabled': value > willpowerMax
                                }"
                                :disabled="value > willpowerMax"
                                @click="setTrackCurrent('willpower', value)"
                                :title="`Força de Vontade temporária: ${value}`"
                              ></button>
                            </div>
                            <small class="text-muted d-block mt-2">
                              Calculada automaticamente a partir de Consciência, Autocontrole e Coragem.
                            </small>
                          </div>
                        </div>
                        <div class="col-md-4">
                          <h6 class="text-uppercase small fw-bold text-muted mb-3">Pontos de Sangue</h6>
                          <div class="track-section">
                            <div class="track-section__label">Reserva máxima ({{ bloodPoolMax }})</div>
                            <div class="dot-rating dot-rating--readonly" role="presentation">
                              <span
                                v-for="value in bloodPoolMax"
                                :key="`blood-max-${value}`"
                                class="dot"
                                :class="{ active: bloodPoolMax >= value }"
                              ></span>
                            </div>
                            <div class="track-section__label mt-3">Disponíveis ({{ form.bloodPool?.current || 0 }})</div>
                            <div class="dot-rating" role="group" aria-label="Definir Pontos de Sangue disponíveis">
                              <button
                                v-for="value in bloodPoolMax"
                                :key="`blood-current-${value}`"
                                type="button"
                                class="dot"
                                :class="{ active: (form.bloodPool?.current || 0) >= value }"
                                @click="setTrackCurrent('bloodPool', value)"
                                :title="`Pontos de Sangue: ${value}`"
                              ></button>
                            </div>
                            <small class="text-muted d-block mt-2">
                              Definidos automaticamente pela geração. Gasto por turno: {{ bloodPerTurn }}.
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <hr class="my-4">

                  <div class="row g-3">
                    <div class="col-lg-6">
                      <div class="selection-panel">
                        <div class="selection-panel__header">
                          <label class="form-label mb-0">Qualidades</label>
                          <span class="selection-panel__meta">Custo total: {{ qualityCostTotal }} | Bônus restantes: {{ availableBonusPoints }}</span>
                        </div>
                        <div class="choice-grid">
                          <label
                            v-for="quality in qualityOptions"
                            :key="quality.id"
                            class="choice-item"
                            :class="{ 'choice-item--active': selectedQualities.includes(quality.id) }"
                            :for="`quality-${quality.id}`"
                          >
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`quality-${quality.id}`"
                              :value="quality.id"
                              v-model="selectedQualities"
                            >
                            <div class="choice-item-content">
                              <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                                <span class="choice-item-title">{{ quality.name }}</span>
                                <span class="badge bg-dark text-warning">-{{ quality.cost }} pts</span>
                              </div>
                              <p class="choice-item-description mb-0">{{ quality.description }}</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="col-lg-6">
                      <div class="selection-panel selection-panel--alt">
                        <div class="selection-panel__header">
                          <label class="form-label mb-0">Defeitos</label>
                          <span class="selection-panel__meta">Total escolhido: {{ defectPointsTotal }}/{{ maxDefectPoints }}</span>
                        </div>
                        <div class="choice-grid">
                          <label
                            v-for="defect in defectOptions"
                            :key="defect.id"
                            class="choice-item"
                            :class="{ 'choice-item--active': selectedDefects.includes(defect.id) }"
                            :for="`defect-${defect.id}`"
                          >
                            <input
                              class="form-check-input"
                              type="checkbox"
                              :id="`defect-${defect.id}`"
                              :value="defect.id"
                              v-model="selectedDefects"
                            >
                            <div class="choice-item-content">
                              <div class="d-flex justify-content-between align-items-start gap-2 mb-1">
                                <span class="choice-item-title">{{ defect.name }}</span>
                                <span class="badge bg-success text-dark">+{{ defect.points }} pts</span>
                              </div>
                              <p class="choice-item-description mb-0">{{ defect.description }}</p>
                            </div>
                          </label>
                        </div>
                      </div>
                    </div>
                    <div class="col-12">
                      <div class="bonus-summary">
                        <span>
                          <i class="bi bi-trophy me-1"></i>
                          Pontos bônus ganhos: {{ defectPointsTotal }}
                        </span>
                        <span>
                          <i class="bi bi-stars me-1"></i>
                          Custos de qualidades: {{ qualityCostTotal }}
                        </span>
                        <span>
                          <i class="bi bi-coin me-1"></i>
                          Bônus disponíveis: {{ availableBonusPoints }}
                        </span>
                      </div>
                    </div>
                    <div class="col-12" v-if="allocationMessages.bonus">
                      <div class="alert alert-warning mt-2 py-2 px-3 small">
                        {{ allocationMessages.bonus }}
                      </div>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Méritos</label>
                      <textarea class="form-control" rows="3" v-model="meritsText" placeholder="Um por linha"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Falhas</label>
                      <textarea class="form-control" rows="3" v-model="flawsText" placeholder="Uma por linha"></textarea>
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">Antecedentes</label>
                      <div class="d-flex justify-content-between align-items-center mb-2">
                        <span class="text-muted small" v-if="!isExperienceMode">Distribua até {{ backgroundLimit }} pontos.</span>
                        <span class="text-muted small" v-else>
                          Antecedentes além do limite inicial exigem XP.
                        </span>
                        <span class="badge bg-dark text-warning fw-normal">
                          <template v-if="!isExperienceMode">
                            {{ backgroundPointsSpent }}/{{ backgroundLimit }} pontos
                          </template>
                          <template v-else>
                            {{ backgroundPointsSpent }} pontos totais (limite inicial: {{ backgroundLimit }})
                          </template>
                        </span>
                      </div>
                      <div v-for="(background, index) in form.backgrounds" :key="`${background.name}-${index}`" class="discipline-row">
                        <div class="d-flex justify-content-between align-items-center">
                          <strong>{{ background.name }}</strong>
                          <div class="d-flex align-items-center gap-2">
                            <div class="dot-rating" role="group" :aria-label="`Definir ${background.name}`">
                              <button
                                v-for="value in 5"
                                :key="value"
                                type="button"
                            class="dot"
                            :class="{ active: background.rating >= value }"
                            @click="setBackgroundRating(index, value)"
                            :title="`${background.name}: ${value}`"
                                :disabled="isCreationLocked && !isExperienceMode"
                              ></button>
                            </div>
                            <button
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                              @click="removeBackground(index)"
                              :disabled="isCreationLocked && !isExperienceMode"
                            >
                              <i class="bi bi-x"></i>
                            </button>
                          </div>
                    </div>
                      </div>
                      <div class="input-group mt-2">
                        <input
                          type="text"
                          class="form-control"
                          v-model="newBackgroundName"
                          placeholder="Adicionar antecedente"
                          :disabled="isCreationLocked && !isExperienceMode"
                        >
                        <button
                          class="btn btn-outline-light"
                          type="button"
                          @click="addBackground"
                          :disabled="!newBackgroundName || (isCreationLocked && !isExperienceMode)"
                        >
                          <i class="bi bi-plus-lg"></i>
                        </button>
                      </div>
                    </div>
                    <div class="col-12" v-if="allocationMessages.backgrounds">
                      <div class="alert alert-warning mt-2 py-2 px-3 small">
                        {{ allocationMessages.backgrounds }}
                      </div>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Experiência total</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="form.experience.total"
                        readonly
                      >
                      <small class="text-muted d-block mt-1">
                        Definida pelo narrador durante as sessões. A edição manual está bloqueada.
                      </small>
                    </div>
                    <div class="col-md-3">
                      <label class="form-label">Experiência gasta</label>
                      <input
                        type="number"
                        class="form-control"
                        v-model.number="form.experience.spent"
                        readonly
                      >
                      <small class="text-muted d-block mt-1">
                        XP disponível: {{ availableExperience }} ponto<span v-if="availableExperience !== 1">s</span>.
                      </small>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Inventário</label>
                      <textarea class="form-control" rows="3" v-model="form.inventory"></textarea>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Descrição</label>
                      <textarea class="form-control" rows="3" v-model="form.description"></textarea>
                    </div>
                    <div class="col-12">
                      <label class="form-label">História</label>
                      <textarea class="form-control" rows="4" v-model="form.history"></textarea>
                    </div>
                    <div class="col-12">
                      <label class="form-label">Notas adicionais</label>
                      <textarea class="form-control" rows="2" v-model="form.notes"></textarea>
                    </div>
                  </div>

                  <div class="d-flex justify-content-end gap-2 mt-4">
                    <button type="button" class="btn btn-outline-danger" v-if="selectedSheetId" @click="confirmDeleteById(selectedSheetId)" :disabled="saving">
                      <i class="bi bi-trash me-2"></i>
                      Excluir ficha
                    </button>
                    <button type="button" class="btn btn-outline-light" @click="printSheet" :disabled="saving">
                      <i class="bi bi-printer me-2"></i>
                      Imprimir ficha
                    </button>
                    <button type="submit" class="btn btn-vampire" :disabled="saving || (!selectedSheetId && isAtLimit)">
                      <span v-if="saving" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      <i v-else class="bi bi-save2 me-2"></i>
                      {{ selectedSheetId ? 'Salvar alterações' : 'Criar ficha' }}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="print-layout" aria-hidden="true">
      <section class="print-page print-page--front">
        <div class="print-overlay">
          <div class="print-header">
            <div>
              <h2 class="print-title">{{ form.name || 'Ficha de Personagem' }}</h2>
              <p class="print-subtitle">{{ form.concept || 'Conceito não informado' }}</p>
            </div>
            <div class="print-meta-grid">
              <span><strong>Crônica:</strong> {{ form.chronicle || '—' }}</span>
              <span><strong>Seita:</strong> {{ selectedSectName }}</span>
              <span><strong>Clã:</strong> {{ selectedClanName }}</span>
              <span><strong>Geração:</strong> {{ form.generation }}</span>
              <span><strong>Status:</strong> {{ form.status === 'final' ? 'Definitiva' : 'Rascunho' }}</span>
            </div>
          </div>

          <div class="print-section">
            <h5 class="print-section__title">Atributos</h5>
            <div class="print-grid">
              <div v-for="category in attributeSchema" :key="category.key" class="print-column">
                <h6 class="print-column__title">{{ category.label }}</h6>
                <div
                  v-for="field in category.fields"
                  :key="`${category.key}-${field.key}`"
                  class="print-row"
                >
                  <span class="print-row__label">{{ field.label }}</span>
                  <div class="print-dots" role="presentation">
                    <span
                      v-for="value in 5"
                      :key="`${category.key}-${field.key}-dot-${value}`"
                      class="print-dot"
                      :class="{ 'print-dot--filled': (form.attributes?.[category.key]?.[field.key] || 0) >= value }"
                    ></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="print-section">
            <h5 class="print-section__title">Habilidades</h5>
            <div class="print-grid">
              <div v-for="group in abilitySchema" :key="group.key" class="print-column">
                <h6 class="print-column__title">{{ group.label }}</h6>
                <div
                  v-for="field in group.fields"
                  :key="`${group.key}-${field.key}`"
                  class="print-row"
                >
                  <span class="print-row__label">{{ field.label }}</span>
                  <div class="print-dots" role="presentation">
                    <span
                      v-for="value in 5"
                      :key="`${group.key}-${field.key}-dot-${value}`"
                      class="print-dot"
                      :class="{ 'print-dot--filled': (form.abilities?.[group.key]?.[field.key] || 0) >= value }"
                    ></span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="print-section print-section--inline">
            <div class="print-column">
              <h6 class="print-column__title">Virtudes</h6>
              <div v-for="virtue in virtueSchema" :key="virtue.key" class="print-row">
                <span class="print-row__label">{{ virtue.label }}</span>
                <div class="print-dots" role="presentation">
                  <span
                    v-for="value in 5"
                    :key="`${virtue.key}-${value}`"
                    class="print-dot"
                    :class="{ 'print-dot--filled': (form.virtues?.[virtue.key] || 0) >= value }"
                  ></span>
                </div>
              </div>
            </div>
            <div class="print-column">
              <h6 class="print-column__title">Humanidade</h6>
              <div class="print-row">
                <span class="print-row__label">Valor</span>
                <div class="print-dots" role="presentation">
                  <span v-for="value in humanityMax" :key="`humanity-${value}`" class="print-dot" :class="{ 'print-dot--filled': humanityValue >= value }"></span>
                </div>
              </div>
              <h6 class="print-column__title">Força de Vontade</h6>
              <div class="print-row">
                <span class="print-row__label">Máxima</span>
                <div class="print-dots" role="presentation">
                  <span v-for="value in willpowerMax" :key="`willpower-max-${value}`" class="print-dot" :class="{ 'print-dot--filled': willpowerValue >= value }"></span>
                </div>
              </div>
              <div class="print-row">
                <span class="print-row__label">Atual</span>
                <div class="print-dots" role="presentation">
                  <span v-for="value in willpowerMax" :key="`willpower-current-${value}`" class="print-dot" :class="{ 'print-dot--filled': (form.willpower?.current || 0) >= value }"></span>
                </div>
              </div>
              <h6 class="print-column__title">Sangue</h6>
              <div class="print-row">
                <span class="print-row__label">Reserva</span>
                <div class="print-dots" role="presentation">
                  <span
                    v-for="value in bloodPoolMax"
                    :key="`blood-${value}`"
                    class="print-dot"
                    :class="{ 'print-dot--filled': (form.bloodPool?.current || 0) >= value }"
                  ></span>
                </div>
              </div>
            </div>
          </div>

          <div class="print-section">
            <h5 class="print-section__title">Disciplinas</h5>
            <div class="print-list" v-if="form.disciplines?.length">
              <div v-for="discipline in form.disciplines" :key="discipline.name" class="print-list__item">
                <span>{{ discipline.name }}</span>
                <div class="print-dots" role="presentation">
                  <span
                    v-for="value in 5"
                    :key="`${discipline.name}-dot-${value}`"
                    class="print-dot"
                    :class="{ 'print-dot--filled': (discipline.rating || 0) >= value }"
                  ></span>
                </div>
              </div>
            </div>
            <p v-else class="print-empty">Nenhuma disciplina adicionada.</p>
          </div>
        </div>
      </section>

      <section class="print-page print-page--back">
        <div class="print-overlay">
          <div class="print-section print-section--columns">
            <div class="print-box">
              <div class="print-box__header">
                <h5 class="print-section__title mb-0">Qualidades</h5>
                <span class="print-chip">-{{ qualityCostTotal }} pts</span>
              </div>
              <ul class="print-box__list" v-if="selectedQualityDetails.length">
                <li v-for="quality in selectedQualityDetails" :key="quality.id" class="print-box__item">
                  <div class="print-box__content">
                    <strong>{{ quality.name }}</strong>
                    <p class="print-text mb-0">{{ quality.description }}</p>
                  </div>
                  <div class="print-dot-row" role="presentation">
                    <span
                      v-for="value in getDotCount(quality.cost)"
                      :key="`${quality.id}-dot-${value}`"
                      class="print-dot"
                      :class="{ 'print-dot--filled': value <= Math.abs(quality.cost || 0) }"
                    ></span>
                  </div>
                </li>
              </ul>
              <p v-else class="print-empty">Nenhuma qualidade selecionada.</p>
            </div>

            <div class="print-box print-box--alt">
              <div class="print-box__header">
                <h5 class="print-section__title mb-0">Defeitos</h5>
                <span class="print-chip print-chip--positive">+{{ defectPointsTotal }} pts</span>
              </div>
              <ul class="print-box__list" v-if="selectedDefectDetails.length">
                <li v-for="defect in selectedDefectDetails" :key="defect.id" class="print-box__item">
                  <div class="print-box__content">
                    <strong>{{ defect.name }}</strong>
                    <p class="print-text mb-0">{{ defect.description }}</p>
                  </div>
                  <div class="print-dot-row" role="presentation">
                    <span
                      v-for="value in getDotCount(defect.points)"
                      :key="`${defect.id}-dot-${value}`"
                      class="print-dot"
                      :class="{ 'print-dot--filled': value <= Math.abs(defect.points || 0) }"
                    ></span>
                  </div>
                </li>
              </ul>
              <p v-else class="print-empty">Nenhum defeito selecionado.</p>
            </div>
          </div>

          <div class="print-section print-section--notes print-section--summary">
            <div>
              <h5 class="print-section__title">Méritos</h5>
              <p class="print-text">{{ meritsText || '—' }}</p>
            </div>
            <div>
              <h5 class="print-section__title">Falhas</h5>
              <p class="print-text">{{ flawsText || '—' }}</p>
            </div>
            <div class="print-stat-card">
              <h5 class="print-section__title mb-1">Experiência</h5>
              <p class="print-text mb-1">Total: {{ form.experience?.total || 0 }} XP</p>
              <p class="print-text mb-1">Gasto: {{ form.experience?.spent || 0 }} XP</p>
              <p class="print-text mb-0">Disponível: {{ availableExperience }} XP</p>
            </div>
          </div>

          <div class="print-section">
            <h5 class="print-section__title">Antecedentes</h5>
            <div class="print-list" v-if="form.backgrounds?.length">
              <div v-for="background in form.backgrounds" :key="background.name" class="print-list__item">
                <span>{{ background.name }}</span>
                <div class="print-dots" role="presentation">
                  <span
                    v-for="value in 5"
                    :key="`${background.name}-dot-${value}`"
                    class="print-dot"
                    :class="{ 'print-dot--filled': (background.rating || 0) >= value }"
                  ></span>
                </div>
              </div>
            </div>
            <p v-else class="print-empty">Nenhum antecedente registrado.</p>
          </div>

          <div class="print-section print-section--notes">
            <div>
              <h5 class="print-section__title">Inventário</h5>
              <p class="print-text">{{ form.inventory || 'Sem itens listados.' }}</p>
            </div>
            <div>
              <h5 class="print-section__title">Descrição</h5>
              <p class="print-text">{{ form.description || 'Sem descrição.' }}</p>
            </div>
          </div>

          <div class="print-section print-section--notes">
            <div>
              <h5 class="print-section__title">História</h5>
              <p class="print-text">{{ form.history || 'Sem história adicionada ainda.' }}</p>
            </div>
            <div>
              <h5 class="print-section__title">Notas adicionais</h5>
              <p class="print-text">{{ form.notes || 'Nenhuma nota extra.' }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { clansData } from '../data/clans'
import { sectsData } from '../data/sects'
import {
  generationOptions,
  qualityOptions,
  defectOptions,
  MAX_DEFECT_POINTS,
  ATTRIBUTE_PRIORITY_POINTS,
  ABILITY_PRIORITY_POINTS,
  BONUS_COSTS
} from '../data/characterCreation'
import {
  fetchCharacterSheets,
  createCharacterSheet,
  updateCharacterSheet,
  deleteCharacterSheet
} from '../services/characterSheets'

const attributeSchema = [
  {
    key: 'physical',
    label: 'Atributos Físicos',
    fields: [
      { key: 'strength', label: 'Força' },
      { key: 'dexterity', label: 'Destreza' },
      { key: 'stamina', label: 'Vigor' }
    ]
  },
  {
    key: 'social',
    label: 'Atributos Sociais',
    fields: [
      { key: 'charisma', label: 'Carisma' },
      { key: 'manipulation', label: 'Manipulação' },
      { key: 'appearance', label: 'Aparência' }
    ]
  },
  {
    key: 'mental',
    label: 'Atributos Mentais',
    fields: [
      { key: 'perception', label: 'Percepção' },
      { key: 'intelligence', label: 'Inteligência' },
      { key: 'wits', label: 'Raciocínio' }
    ]
  }
]

const abilitySchema = [
  {
    key: 'talents',
    label: 'Talentos',
    fields: [
      { key: 'alertness', label: 'Prontidão' },
      { key: 'athletics', label: 'Esportes' },
      { key: 'brawl', label: 'Briga' },
      { key: 'empathy', label: 'Empatia' },
      { key: 'expression', label: 'Expressão' },
      { key: 'intimidation', label: 'Intimidação' },
      { key: 'leadership', label: 'Liderança' },
      { key: 'streetwise', label: 'Rua' },
      { key: 'subterfuge', label: 'Subterfúgio' }
    ]
  },
  {
    key: 'skills',
    label: 'Perícias',
    fields: [
      { key: 'animalKen', label: 'Trato com Animais' },
      { key: 'crafts', label: 'Ofícios' },
      { key: 'drive', label: 'Condução' },
      { key: 'etiquette', label: 'Etiqueta' },
      { key: 'firearms', label: 'Armas de Fogo' },
      { key: 'larceny', label: 'Furto' },
      { key: 'melee', label: 'Armas Brancas' },
      { key: 'performance', label: 'Performance' },
      { key: 'stealth', label: 'Furtividade' },
      { key: 'survival', label: 'Sobrevivência' }
    ]
  },
  {
    key: 'knowledges',
    label: 'Conhecimentos',
    fields: [
      { key: 'academics', label: 'Acadêmicos' },
      { key: 'computer', label: 'Computador' },
      { key: 'finance', label: 'Finanças' },
      { key: 'investigation', label: 'Investigação' },
      { key: 'law', label: 'Direito' },
      { key: 'medicine', label: 'Medicina' },
      { key: 'occult', label: 'Ocultismo' },
      { key: 'politics', label: 'Política' },
      { key: 'science', label: 'Ciência' },
      { key: 'technology', label: 'Tecnologia' }
    ]
  }
]

const virtueSchema = [
  { key: 'conscience', label: 'Consciência' },
  { key: 'selfControl', label: 'Autocontrole' },
  { key: 'courage', label: 'Coragem' }
]

const HEALTH_LEVELS = [
  { key: 'bruised', label: 'Escoriado', penalty: '' },
  { key: 'hurt', label: 'Machucado', penalty: '-1' },
  { key: 'injured', label: 'Ferido', penalty: '-1' },
  { key: 'wounded', label: 'Ferido gravemente', penalty: '-2' },
  { key: 'mauled', label: 'Espancado', penalty: '-2' },
  { key: 'crippled', label: 'Aleijado', penalty: '-5' },
  { key: 'incapacitated', label: 'Incapacitado', penalty: '' }
]

const attributePriorityOptions = [
  { value: 'primary', label: 'Primário (7 pontos)' },
  { value: 'secondary', label: 'Secundário (5 pontos)' },
  { value: 'tertiary', label: 'Terciário (3 pontos)' }
]

const abilityPriorityOptions = [
  { value: 'primary', label: 'Primária (13 pontos)' },
  { value: 'secondary', label: 'Secundária (9 pontos)' },
  { value: 'tertiary', label: 'Terciária (5 pontos)' }
]

const BASE_ATTRIBUTE_RATING = 1
const BASE_VIRTUE_RATING = 1
const BONUS_DEFICIT_MESSAGE = 'Você gastou mais pontos de bônus do que possui. Ajuste sua distribuição.'
const DEFECT_LIMIT_MESSAGE = `Você só pode selecionar até ${MAX_DEFECT_POINTS} pontos de defeitos.`
const QUALITIES_NEED_DEFECTS_MESSAGE = 'Selecione mais defeitos para pagar por essas qualidades.'

function createEmptySheet() {
  return {
    name: '',
    concept: '',
    chronicle: '',
    sect: '',
    clan: '',
    generation: 13,
    lineage: '',
    status: 'draft',
    attributes: JSON.parse(JSON.stringify({
      physical: { strength: 1, dexterity: 1, stamina: 1 },
      social: { charisma: 1, manipulation: 1, appearance: 1 },
      mental: { perception: 1, intelligence: 1, wits: 1 }
    })),
    abilities: JSON.parse(JSON.stringify({
      talents: {
        alertness: 0,
        athletics: 0,
        brawl: 0,
        empathy: 0,
        expression: 0,
        intimidation: 0,
        leadership: 0,
        streetwise: 0,
        subterfuge: 0
      },
      skills: {
        animalKen: 0,
        crafts: 0,
        drive: 0,
        etiquette: 0,
        firearms: 0,
        larceny: 0,
        melee: 0,
        performance: 0,
        stealth: 0,
        survival: 0
      },
      knowledges: {
        academics: 0,
        computer: 0,
        finance: 0,
        investigation: 0,
        law: 0,
        medicine: 0,
        occult: 0,
        politics: 0,
        science: 0,
        technology: 0
      }
    })),
    disciplines: [],
    virtues: { conscience: 1, selfControl: 1, courage: 1 },
    experience: { total: 0, spent: 0 },
    health: { max: HEALTH_LEVELS.length, current: 0 },
    willpower: { max: 10, current: 3 },
    bloodPool: { max: 10, perTurn: 1, current: 0 },
    inventory: '',
    description: '',
    history: '',
    backgrounds: [],
    qualities: [],
    flaws: [],
    merits: [],
    defects: [],
    creation: {
      attributePriorities: { physical: 'primary', social: 'secondary', mental: 'tertiary' },
      abilityPriorities: { talents: 'primary', skills: 'secondary', knowledges: 'tertiary' },
      bonusAllocations: {
        attributes: { physical: 0, social: 0, mental: 0 },
        abilities: { talents: 0, skills: 0, knowledges: 0 },
        disciplines: 0,
        backgrounds: 0
      }
    },
    notes: ''
  }
}

function createExperienceBaselines() {
  return {
    attributes: {},
    abilities: {},
    disciplines: [],
    backgrounds: [],
    qualities: [],
    defects: [],
    experienceSpent: 0
  }
}

function formatList(list = []) {
  return Array.isArray(list) ? list.join('\n') : ''
}

function parseList(text = '') {
  return text
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean)
}

export default {
  name: 'CharacterSheetManager',
  props: {
    user: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      attributeSchema,
      abilitySchema,
      virtueSchema,
      healthLevels: HEALTH_LEVELS,
      generationOptions,
      qualityOptions,
      defectOptions,
      attributePriorityOptions,
      abilityPriorityOptions,
      maxDefectPoints: MAX_DEFECT_POINTS,
      statusOptions: [
        { value: 'draft', label: 'Rascunho — edição livre' },
        { value: 'final', label: 'Definitiva — bloqueia redistribuição' }
      ],
      sheets: [],
      loading: false,
      saving: false,
      error: '',
      success: '',
      selectedSheetId: null,
      form: createEmptySheet(),
      originalStatus: 'draft',
      meritsText: '',
      flawsText: '',
      newDisciplineName: '',
      newBackgroundName: '',
      selectedQualities: [],
      selectedDefects: [],
      lastValidQualitySelection: [],
      lastValidDefectSelection: [],
      allocationMessages: {
        attributes: '',
        abilities: '',
        disciplines: '',
        backgrounds: '',
        virtues: '',
        bonus: ''
      },
      allocationMessageTimers: {},
      experienceBaselines: createExperienceBaselines(),
      isHydratingForm: false
    }
  },
  computed: {
    availableSects() {
      return Object.values(sectsData).map((sect) => ({ id: sect.id, name: sect.name }))
    },
    availableClans() {
      if (!this.form.sect) {
        return []
      }
      const group = clansData[this.form.sect]
      return group ? group.clans : []
    },
    selectedSectName() {
      const sect = this.availableSects.find((item) => item.id === this.form.sect)
      return sect?.name || '—'
    },
    selectedClanName() {
      const clan = this.availableClans.find((item) => item.id === this.form.clan)
      return clan?.name || '—'
    },
    sheetLimit() {
      const type = this.user?.account_type || 'free'
      return type === 'premium' ? 5 : 1
    },
    remainingSlots() {
      return Math.max(0, this.sheetLimit - this.sheets.length)
    },
    isAtLimit() {
      return this.sheets.length >= this.sheetLimit
    },
    accountTypeLabel() {
      return this.user?.account_type === 'premium' ? 'Premium' : 'Gratuito'
    },
    clanDisciplineNames() {
      const selectedClan = this.availableClans.find((clan) => clan.id === this.form.clan)
      const base = new Set()
      if (selectedClan && Array.isArray(selectedClan.disciplines)) {
        selectedClan.disciplines.forEach((name) => base.add(String(name).toLowerCase()))
      }
      return base
    },
    attributePriorities() {
      return this.form.creation?.attributePriorities || { physical: 'primary', social: 'secondary', mental: 'tertiary' }
    },
    abilityPriorities() {
      return this.form.creation?.abilityPriorities || { talents: 'primary', skills: 'secondary', knowledges: 'tertiary' }
    },
    bonusAllocations() {
      const defaults = {
        attributes: { physical: 0, social: 0, mental: 0 },
        abilities: { talents: 0, skills: 0, knowledges: 0 },
        disciplines: 0,
        backgrounds: 0
      }
      return this.form.creation?.bonusAllocations || defaults
    },
    attributeLimits() {
      return {
        physical:
          (ATTRIBUTE_PRIORITY_POINTS[this.attributePriorities.physical] || 0) + (this.bonusAllocations.attributes?.physical || 0),
        social:
          (ATTRIBUTE_PRIORITY_POINTS[this.attributePriorities.social] || 0) + (this.bonusAllocations.attributes?.social || 0),
        mental:
          (ATTRIBUTE_PRIORITY_POINTS[this.attributePriorities.mental] || 0) + (this.bonusAllocations.attributes?.mental || 0)
      }
    },
    abilityLimits() {
      return {
        talents:
          (ABILITY_PRIORITY_POINTS[this.abilityPriorities.talents] || 0) + (this.bonusAllocations.abilities?.talents || 0),
        skills:
          (ABILITY_PRIORITY_POINTS[this.abilityPriorities.skills] || 0) + (this.bonusAllocations.abilities?.skills || 0),
        knowledges:
          (ABILITY_PRIORITY_POINTS[this.abilityPriorities.knowledges] || 0) +
          (this.bonusAllocations.abilities?.knowledges || 0)
      }
    },
    attributePointsSpent() {
      return this.attributeSchema.reduce((acc, category) => {
        const values = this.form.attributes?.[category.key] || {}
        const total = Object.values(values).reduce((sum, value) => sum + Math.max(0, Number(value) - BASE_ATTRIBUTE_RATING), 0)
        acc[category.key] = total
        return acc
      }, {})
    },
    abilityPointsSpent() {
      return this.abilitySchema.reduce((acc, group) => {
        const values = this.form.abilities?.[group.key] || {}
        const total = Object.values(values).reduce((sum, value) => sum + Math.max(0, Number(value) || 0), 0)
        acc[group.key] = total
        return acc
      }, {})
    },
    disciplinePointsSpent() {
      return (this.form.disciplines || []).reduce((sum, discipline) => sum + Math.max(0, Number(discipline.rating) || 0), 0)
    },
    disciplineLimit() {
      return 3 + (this.bonusAllocations.disciplines || 0)
    },
    backgroundPointsSpent() {
      return (this.form.backgrounds || []).reduce((sum, background) => sum + Math.max(0, Number(background.rating) || 0), 0)
    },
    backgroundLimit() {
      return 5 + (this.bonusAllocations.backgrounds || 0)
    },
    virtuePointsSpent() {
      const virtues = this.form.virtues || {}
      return Object.values(virtues).reduce(
        (sum, value) => sum + Math.max(0, Number(value) - BASE_VIRTUE_RATING),
        0
      )
    },
    humanityValue() {
      const virtues = this.form.virtues || {}
      return (Number(virtues.conscience) || 0) + (Number(virtues.selfControl) || 0)
    },
    willpowerValue() {
      const virtues = this.form.virtues || {}
      const courage = Number(virtues.courage) || 0
      return Math.max(0, Math.min(this.humanityMax, courage))
    },
    selectedQualityDetails() {
      const map = new Map(qualityOptions.map((quality) => [quality.id, quality]))
      return this.selectedQualities
        .map((id) => map.get(id))
        .filter(Boolean)
        .map((item) => ({ id: item.id, name: item.name, cost: item.cost, description: item.description }))
    },
    selectedDefectDetails() {
      const map = new Map(defectOptions.map((defect) => [defect.id, defect]))
      return this.selectedDefects
        .map((id) => map.get(id))
        .filter(Boolean)
        .map((item) => ({ id: item.id, name: item.name, points: item.points, description: item.description }))
    },
    qualityCostTotal() {
      return this.selectedQualityDetails.reduce((sum, quality) => sum + Number(quality.cost || 0), 0)
    },
    defectPointsTotal() {
      return this.selectedDefectDetails.reduce((sum, defect) => sum + Number(defect.points || 0), 0)
    },
    bonusSpent() {
      const attributeCost = Object.values(this.bonusAllocations.attributes || {}).reduce(
        (sum, dots) => sum + Number(dots || 0) * BONUS_COSTS.attributes,
        0
      )
      const abilityCost = Object.values(this.bonusAllocations.abilities || {}).reduce(
        (sum, dots) => sum + Number(dots || 0) * BONUS_COSTS.abilities,
        0
      )
      const disciplineCost = Number(this.bonusAllocations.disciplines || 0) * BONUS_COSTS.disciplines
      const backgroundCost = Number(this.bonusAllocations.backgrounds || 0) * BONUS_COSTS.backgrounds
      return attributeCost + abilityCost + disciplineCost + backgroundCost
    },
    bonusBalance() {
      return this.defectPointsTotal - this.qualityCostTotal - this.bonusSpent
    },
    humanityMax() {
      return 10
    },
    willpowerMax() {
      const virtues = this.form.virtues || {}
      const courage = Number(virtues.courage) || 0
      return Math.max(1, Math.min(this.humanityMax, courage))
    },
    bloodPoolMax() {
      return Math.max(1, this.currentGenerationStats?.bloodPool?.max || 10)
    },
    bloodPerTurn() {
      return Math.max(1, this.currentGenerationStats?.bloodPool?.perTurn || 1)
    },
    generationBackgroundEntry() {
      return this.findGenerationBackground()
    },
    canEditGeneration() {
      return Boolean(this.generationBackgroundEntry)
    },
    availableBonusPoints() {
      return Math.max(0, this.bonusBalance)
    },
    bonusDeficit() {
      return Math.min(0, this.bonusBalance)
    },
    isFinalStatus() {
      return String(this.form.status || '').toLowerCase() === 'final'
    },
    isExperienceMode() {
      return this.isFinalStatus && Boolean(this.selectedSheetId)
    },
    isCreationLocked() {
      if (!this.isFinalStatus) {
        return false
      }
      return Boolean(this.selectedSheetId)
    },
    availableExperience() {
      const total = Number(this.form.experience?.total) || 0
      const spent = Number(this.form.experience?.spent) || 0
      return Math.max(0, total - spent)
    },
    currentGenerationStats() {
      return generationOptions.find((option) => option.value === this.form.generation) || generationOptions[0]
    }
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser?.id) {
          this.loadSheets()
        } else {
          this.sheets = []
          this.resetForm()
        }
      }
    },
    'form.sect'(newValue) {
      if (this.isHydratingForm) {
        return
      }

      if (!newValue) {
        this.form.clan = ''
        this.form.disciplines = []
        return
      }

      const clans = clansData[newValue]?.clans || []
      if (!clans.some((clan) => clan.id === this.form.clan)) {
        this.form.clan = ''
        this.form.disciplines = []
      }
    },
    'form.clan'(newValue) {
      if (this.isHydratingForm) {
        return
      }
      if (!newValue) {
        this.form.disciplines = []
        return
      }
      this.applyClanDisciplines(newValue)
    },
    'form.generation': {
      immediate: true,
      handler() {
        this.handleGenerationChange()
      }
    },
    selectedDefects(newValue) {
      if (this.isHydratingForm) {
        this.lastValidDefectSelection = [...newValue]
        return
      }
      if (this.isFinalStatus) {
        const lockedDefects = new Set(this.experienceBaselines.defects || [])
        const removedLocked = Array.from(lockedDefects).filter((id) => !newValue.includes(id))
        if (removedLocked.length) {
          this.setAllocationMessage(
            'bonus',
            'Defeitos já consolidados não podem ser removidos após a ficha definitiva.'
          )
          this.$nextTick(() => {
            this.selectedDefects = [...this.lastValidDefectSelection]
          })
          return
        }
      }
      if (this.defectPointsTotal > this.maxDefectPoints) {
        this.setAllocationMessage('bonus', DEFECT_LIMIT_MESSAGE)
        this.$nextTick(() => {
          this.selectedDefects = [...this.lastValidDefectSelection]
        })
        return
      }
      this.lastValidDefectSelection = [...newValue]
      if (this.allocationMessages.bonus === DEFECT_LIMIT_MESSAGE) {
        this.setAllocationMessage('bonus', '')
      }
    },
    selectedQualities(newValue) {
      if (this.isHydratingForm) {
        this.lastValidQualitySelection = [...newValue]
        return
      }
      if (this.isFinalStatus) {
        const lockedQualities = new Set(this.experienceBaselines.qualities || [])
        const removedLocked = Array.from(lockedQualities).filter((id) => !newValue.includes(id))
        if (removedLocked.length) {
          this.setAllocationMessage(
            'bonus',
            'Qualidades definitivas não podem ser removidas após o salvamento da ficha.'
          )
          this.$nextTick(() => {
            this.selectedQualities = [...this.lastValidQualitySelection]
          })
          return
        }
      }
      if (this.qualityCostTotal > this.defectPointsTotal) {
        this.setAllocationMessage('bonus', QUALITIES_NEED_DEFECTS_MESSAGE)
        this.$nextTick(() => {
          this.selectedQualities = [...this.lastValidQualitySelection]
        })
        return
      }
      this.lastValidQualitySelection = [...newValue]
      if (this.allocationMessages.bonus === QUALITIES_NEED_DEFECTS_MESSAGE) {
        this.setAllocationMessage('bonus', '')
      }
    },
    bonusBalance: {
      immediate: true,
      handler(newValue) {
        if (this.isHydratingForm) {
          return
        }
        if (newValue < 0) {
          this.setAllocationMessage('bonus', BONUS_DEFICIT_MESSAGE)
        } else if (this.allocationMessages.bonus === BONUS_DEFICIT_MESSAGE) {
          this.setAllocationMessage('bonus', '')
        }
      }
    }
  },
  methods: {
    async loadSheets() {
      if (!this.user?.id) {
        return
      }
      this.loading = true
      this.error = ''
      try {
        const { data } = await fetchCharacterSheets(this.user.id)
        this.sheets = (data.sheets || []).map((sheet) => ({
          ...sheet,
          createdAt: sheet.createdAt,
          updatedAt: sheet.updatedAt,
          status: String(sheet.status || 'draft').toLowerCase()
        }))
      } catch (error) {
        this.error = error.response?.data?.error || 'Não foi possível carregar suas fichas de personagem.'
      } finally {
        this.loading = false
      }
    },
    formatDate(value) {
      if (!value) {
        return '—'
      }
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return '—'
      }
      return date.toLocaleDateString('pt-BR', { year: 'numeric', month: 'short', day: 'numeric' })
    },
    ensureCreationStructure() {
      if (!this.form.creation) {
        const defaults = createEmptySheet().creation
        this.form.creation = JSON.parse(JSON.stringify(defaults))
        return
      }

      const defaults = createEmptySheet().creation
      this.form.creation.attributePriorities = {
        ...defaults.attributePriorities,
        ...(this.form.creation.attributePriorities || {})
      }
      this.form.creation.abilityPriorities = {
        ...defaults.abilityPriorities,
        ...(this.form.creation.abilityPriorities || {})
      }
      const bonus = this.form.creation.bonusAllocations || {}
      this.form.creation.bonusAllocations = {
        attributes: { ...defaults.bonusAllocations.attributes, ...(bonus.attributes || {}) },
        abilities: { ...defaults.bonusAllocations.abilities, ...(bonus.abilities || {}) },
        disciplines: bonus.disciplines || defaults.bonusAllocations.disciplines,
        backgrounds: bonus.backgrounds || defaults.bonusAllocations.backgrounds
      }
    },
    resetExperienceBaselines() {
      this.experienceBaselines = createExperienceBaselines()
    },
    captureExperienceBaselines() {
      const attributeBaseline = {}
      Object.keys(this.form.attributes || {}).forEach((category) => {
        const group = this.form.attributes[category] || {}
        attributeBaseline[category] = {}
        Object.keys(group).forEach((attribute) => {
          attributeBaseline[category][attribute] = Number(group[attribute]) || BASE_ATTRIBUTE_RATING
        })
      })

      const abilityBaseline = {}
      Object.keys(this.form.abilities || {}).forEach((category) => {
        const group = this.form.abilities[category] || {}
        abilityBaseline[category] = {}
        Object.keys(group).forEach((ability) => {
          abilityBaseline[category][ability] = Number(group[ability]) || 0
        })
      })

      const disciplines = (Array.isArray(this.form.disciplines) ? this.form.disciplines : []).map((discipline) => ({
        name: discipline.name,
        normalized: this.normalizeDisciplineName(discipline.name),
        rating: Number(discipline.rating) || 0
      }))

      const backgrounds = (Array.isArray(this.form.backgrounds) ? this.form.backgrounds : []).map((background) => ({
        name: background.name,
        normalized: this.normalizeBackgroundName(background.name),
        rating: Number(background.rating) || 0
      }))

      const experienceSpent = Number(this.form.experience?.spent) || 0

    this.experienceBaselines = {
      attributes: attributeBaseline,
      abilities: abilityBaseline,
      disciplines,
      backgrounds,
      qualities: [...this.selectedQualities],
      defects: [...this.selectedDefects],
      experienceSpent
    }
  },
    setAllocationMessage(section, message) {
      if (this.allocationMessageTimers[section]) {
        clearTimeout(this.allocationMessageTimers[section])
        delete this.allocationMessageTimers[section]
      }
      this.allocationMessages = {
        ...this.allocationMessages,
        [section]: message
      }
      if (message) {
        this.allocationMessageTimers[section] = setTimeout(() => {
          this.allocationMessages = {
            ...this.allocationMessages,
            [section]: ''
          }
        }, 4000)
      }
    },
    getDotCount(value) {
      const normalized = Math.abs(Math.round(Number(value) || 0))
      return Math.max(1, Math.min(10, normalized || 1))
    },
    canSpendExperience(cost) {
      return Number.isFinite(cost) && cost > 0 && cost <= this.availableExperience
    },
    spendExperience(cost) {
      if (!this.canSpendExperience(cost)) {
        return false
      }
      if (!this.form.experience) {
        this.form.experience = { total: 0, spent: 0 }
      }
      const currentSpent = Number(this.form.experience.spent) || 0
      const total = Number(this.form.experience.total) || 0
      const newSpent = Math.min(total, currentSpent + cost)
      this.form.experience.spent = newSpent
      return true
    },
    refundExperience(cost) {
      if (!Number.isFinite(cost) || cost <= 0) {
        return 0
      }
      if (!this.form.experience) {
        this.form.experience = { total: 0, spent: 0 }
      }
      const currentSpent = Number(this.form.experience.spent) || 0
      const baselineSpent = Number(this.experienceBaselines.experienceSpent) || 0
      const newSpent = Math.max(baselineSpent, currentSpent - cost)
      this.form.experience.spent = newSpent
      return currentSpent - newSpent
    },
    getAttributeBaseline(category, attribute) {
      const categoryBaseline = this.experienceBaselines.attributes?.[category]
      if (!categoryBaseline || categoryBaseline[attribute] === undefined) {
        return BASE_ATTRIBUTE_RATING
      }
      return Number(categoryBaseline[attribute]) || BASE_ATTRIBUTE_RATING
    },
    getAbilityBaseline(category, ability) {
      const categoryBaseline = this.experienceBaselines.abilities?.[category]
      if (!categoryBaseline || categoryBaseline[ability] === undefined) {
        return 0
      }
      return Number(categoryBaseline[ability]) || 0
    },
    normalizeDisciplineName(name) {
      return String(name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },
    normalizeBackgroundName(name) {
      return String(name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim()
    },
    getDisciplineBaseline(name) {
      const normalized = this.normalizeDisciplineName(name)
      const entry = (this.experienceBaselines.disciplines || []).find(
        (item) => item.normalized === normalized
      )
      if (!entry) {
        return 0
      }
      return Number(entry.rating) || 0
    },
    getBackgroundBaseline(name) {
      const normalized = this.normalizeBackgroundName(name)
      const entry = (this.experienceBaselines.backgrounds || []).find(
        (item) => item.normalized === normalized
      )
      if (!entry) {
        return 0
      }
      return Number(entry.rating) || 0
    },
    isGenerationBackground(background) {
      if (!background) {
        return false
      }
      return this.normalizeBackgroundName(background.name) === 'geracao'
    },
    findGenerationBackground() {
      if (!Array.isArray(this.form.backgrounds)) {
        return null
      }
      return this.form.backgrounds.find((background) => this.isGenerationBackground(background)) || null
    },
    calculateGenerationFromBackground(rating) {
      const sanitized = Math.max(0, Math.min(5, Number(rating) || 0))
      return Math.max(8, 13 - sanitized)
    },
    calculateBackgroundFromGeneration(generation) {
      const sanitized = Math.max(8, Math.min(13, Number(generation) || 13))
      return Math.max(0, 13 - sanitized)
    },
    syncGenerationBackgroundFromRating() {
      const entry = this.findGenerationBackground()
      if (!entry) {
        if (this.form.generation !== 13) {
          this.form.generation = 13
        }
        return
      }
      const desiredGeneration = this.calculateGenerationFromBackground(entry.rating)
      if (this.form.generation !== desiredGeneration) {
        this.form.generation = desiredGeneration
      }
    },
    syncGenerationBackgroundFromGeneration() {
      const entry = this.findGenerationBackground()
      if (!entry) {
        return
      }
      const desiredRating = this.calculateBackgroundFromGeneration(this.form.generation)
      const limited = Math.max(0, Math.min(5, desiredRating))
      if (Number(entry.rating) !== limited) {
        entry.rating = limited
      }
    },
    syncDerivedValues() {
      if (!this.form) {
        return
      }
      if (!this.form.health || typeof this.form.health !== 'object') {
        this.form.health = { max: HEALTH_LEVELS.length, current: 0 }
      }
      this.form.health.max = HEALTH_LEVELS.length
      const currentHealth = Number(this.form.health.current) || 0
      this.form.health.current = Math.max(0, Math.min(this.form.health.max, currentHealth))

      const maxWillpower = this.willpowerMax
      if (!this.form.willpower || typeof this.form.willpower !== 'object') {
        this.form.willpower = { max: maxWillpower, current: maxWillpower }
      }
      let currentWillpower = Number(this.form.willpower.current)
      if (!Number.isFinite(currentWillpower) || currentWillpower < 0) {
        currentWillpower = 0
      }
      if (currentWillpower > maxWillpower) {
        currentWillpower = maxWillpower
      }
      this.form.willpower.max = maxWillpower
      this.form.willpower.current = currentWillpower

      const bloodStats = this.currentGenerationStats?.bloodPool || { max: 10, perTurn: 1 }
      if (!this.form.bloodPool || typeof this.form.bloodPool !== 'object') {
        this.form.bloodPool = { max: bloodStats.max, perTurn: bloodStats.perTurn, current: bloodStats.max }
      }
      let currentBlood = Number(this.form.bloodPool.current)
      if (!Number.isFinite(currentBlood) || currentBlood < 0) {
        currentBlood = 0
      }
      if (currentBlood > bloodStats.max) {
        currentBlood = bloodStats.max
      }
      this.form.bloodPool.max = bloodStats.max
      this.form.bloodPool.perTurn = bloodStats.perTurn || 1
      this.form.bloodPool.current = currentBlood
    },
    setHealthLevel(index) {
      if (!this.form.health || typeof this.form.health !== 'object') {
        this.form.health = { max: HEALTH_LEVELS.length, current: 0 }
      }
      const max = this.form.health.max || HEALTH_LEVELS.length
      const target = Math.min(max, index + 1)
      const current = Number(this.form.health.current) || 0
      this.form.health.current = current === target ? target - 1 : target
    },
    calculateAttributeExperienceCost(current, target) {
      let cost = 0
      for (let rating = Math.max(BASE_ATTRIBUTE_RATING, current); rating < target; rating += 1) {
        const nextLevel = rating + 1
        cost += nextLevel
      }
      return cost
    },
    calculateAbilityExperienceCost(current, target) {
      let cost = 0
      for (let rating = Math.max(0, current); rating < target; rating += 1) {
        const nextLevel = rating + 1
        cost += nextLevel
      }
      return cost
    },
    calculateDisciplineExperienceCost(current, target) {
      let cost = 0
      for (let rating = Math.max(0, current); rating < target; rating += 1) {
        const nextLevel = rating + 1
        cost += nextLevel + 1
      }
      return cost
    },
    calculateBackgroundExperienceCost(current, target) {
      let cost = 0
      for (let rating = Math.max(0, current); rating < target; rating += 1) {
        const nextLevel = rating + 1
        cost += nextLevel
      }
      return cost
    },
    notifyCreationLocked(section) {
      const messages = {
        attributes: 'Esta ficha está definitiva. Ajuste os atributos apenas com pontos de experiência.',
        abilities: 'Esta ficha está definitiva. Ajuste as habilidades apenas com pontos de experiência.',
        disciplines: 'Esta ficha está definitiva. Ajuste as disciplinas apenas com pontos de experiência.',
        backgrounds: 'Esta ficha está definitiva. Ajuste os antecedentes apenas com pontos de experiência.',
        virtues: 'Esta ficha está definitiva. Ajuste as virtudes apenas com pontos de experiência.'
      }
      const message = messages[section] ||
        'Esta ficha está definitiva. Utilize pontos de experiência para aplicar novas mudanças.'
      this.setAllocationMessage(section, message)
    },
    notifyPriorityReset(section) {
      const messages = {
        attributes: 'Os pontos dessa categoria foram redefinidos com a nova prioridade.',
        abilities: 'Os pontos desse grupo foram redefinidos com a nova prioridade.'
      }
      if (messages[section]) {
        this.setAllocationMessage(section, messages[section])
      }
    },
    resetAttributeCategory(category) {
      const group = this.form.attributes?.[category]
      if (!group) {
        return
      }
      Object.keys(group).forEach((key) => {
        group[key] = BASE_ATTRIBUTE_RATING
      })
      this.cleanupAttributeBonus(category)
    },
    resetAbilityGroup(groupKey) {
      const group = this.form.abilities?.[groupKey]
      if (!group) {
        return
      }
      Object.keys(group).forEach((key) => {
        group[key] = 0
      })
      this.cleanupAbilityBonus(groupKey)
    },
    updateBonusAllocation(section, key, value) {
      this.ensureCreationStructure()
      const allocations = this.form.creation.bonusAllocations
      const sanitizedValue = Math.max(0, Math.floor(Number(value) || 0))
      if (section === 'attributes' || section === 'abilities') {
        allocations[section] = {
          ...allocations[section],
          [key]: sanitizedValue
        }
      } else {
        allocations[section] = sanitizedValue
      }
    },
    cleanupAttributeBonus(category) {
      const baseLimit = ATTRIBUTE_PRIORITY_POINTS[this.attributePriorities[category]] || 0
      const spent = this.attributePointsSpent[category]
      const requiredExtra = Math.max(0, spent - baseLimit)
      if ((this.bonusAllocations.attributes?.[category] || 0) !== requiredExtra) {
        this.updateBonusAllocation('attributes', category, requiredExtra)
      }
    },
    cleanupAbilityBonus(group) {
      const baseLimit = ABILITY_PRIORITY_POINTS[this.abilityPriorities[group]] || 0
      const spent = this.abilityPointsSpent[group]
      const requiredExtra = Math.max(0, spent - baseLimit)
      if ((this.bonusAllocations.abilities?.[group] || 0) !== requiredExtra) {
        this.updateBonusAllocation('abilities', group, requiredExtra)
      }
    },
    cleanupDisciplineBonus() {
      const spent = this.disciplinePointsSpent
      const requiredExtra = Math.max(0, spent - 3)
      if ((this.bonusAllocations.disciplines || 0) !== requiredExtra) {
        this.updateBonusAllocation('disciplines', null, requiredExtra)
      }
    },
    cleanupBackgroundBonus() {
      const spent = this.backgroundPointsSpent
      const requiredExtra = Math.max(0, spent - 5)
      if ((this.bonusAllocations.backgrounds || 0) !== requiredExtra) {
        this.updateBonusAllocation('backgrounds', null, requiredExtra)
      }
    },
    cleanupAllBonuses() {
      this.attributeSchema.forEach((category) => this.cleanupAttributeBonus(category.key))
      this.abilitySchema.forEach((group) => this.cleanupAbilityBonus(group.key))
      this.cleanupDisciplineBonus()
      this.cleanupBackgroundBonus()
    },
    canAllocateAttributePoints(category, delta) {
      const baseLimit = ATTRIBUTE_PRIORITY_POINTS[this.attributePriorities[category]] || 0
      const currentExtra = this.bonusAllocations.attributes?.[category] || 0
      const spent = this.attributePointsSpent[category]
      const newSpent = spent + delta
      if (newSpent <= baseLimit + currentExtra) {
        return true
      }
      const extraNeeded = newSpent - (baseLimit + currentExtra)
      const cost = extraNeeded * BONUS_COSTS.attributes
      if (cost <= this.availableBonusPoints) {
        this.updateBonusAllocation('attributes', category, currentExtra + extraNeeded)
        return true
      }
      return false
    },
    canAllocateAbilityPoints(group, delta) {
      const baseLimit = ABILITY_PRIORITY_POINTS[this.abilityPriorities[group]] || 0
      const currentExtra = this.bonusAllocations.abilities?.[group] || 0
      const spent = this.abilityPointsSpent[group]
      const newSpent = spent + delta
      if (newSpent <= baseLimit + currentExtra) {
        return true
      }
      const extraNeeded = newSpent - (baseLimit + currentExtra)
      const cost = extraNeeded * BONUS_COSTS.abilities
      if (cost <= this.availableBonusPoints) {
        this.updateBonusAllocation('abilities', group, currentExtra + extraNeeded)
        return true
      }
      return false
    },
    canAllocateDisciplinePoints(delta) {
      const currentExtra = this.bonusAllocations.disciplines || 0
      const spent = this.disciplinePointsSpent
      const newSpent = spent + delta
      const baseLimit = 3
      if (newSpent <= baseLimit + currentExtra) {
        return true
      }
      const extraNeeded = newSpent - (baseLimit + currentExtra)
      const cost = extraNeeded * BONUS_COSTS.disciplines
      if (cost <= this.availableBonusPoints) {
        this.updateBonusAllocation('disciplines', null, currentExtra + extraNeeded)
        return true
      }
      return false
    },
    canAllocateBackgroundPoints(delta) {
      const currentExtra = this.bonusAllocations.backgrounds || 0
      const spent = this.backgroundPointsSpent
      const newSpent = spent + delta
      const baseLimit = 5
      if (newSpent <= baseLimit + currentExtra) {
        return true
      }
      const extraNeeded = newSpent - (baseLimit + currentExtra)
      const cost = extraNeeded * BONUS_COSTS.backgrounds
      if (cost <= this.availableBonusPoints) {
        this.updateBonusAllocation('backgrounds', null, currentExtra + extraNeeded)
        return true
      }
      return false
    },
    normalizeSelection(list, options) {
      if (!Array.isArray(list)) {
        return []
      }
      const idsByName = new Map(options.map((item) => [item.name.toLowerCase(), item.id]))
      const ids = list
        .map((entry) => {
          if (!entry) {
            return null
          }
          if (typeof entry === 'string') {
            return idsByName.get(entry.toLowerCase()) || null
          }
          if (typeof entry === 'object') {
            const id = entry.id || idsByName.get(String(entry.name || '').toLowerCase())
            return id || null
          }
          return null
        })
        .filter(Boolean)
      return Array.from(new Set(ids))
    },
    setAttributePriority(category, priority) {
      this.ensureCreationStructure()
      if (this.isCreationLocked) {
        this.notifyCreationLocked('attributes')
        return
      }
      if (!priority) {
        return
      }
      const priorities = this.form.creation.attributePriorities
      if (!priorities || priorities[category] === priority) {
        return
      }
      const previous = priorities[category]
      const conflict = Object.keys(priorities).find((key) => key !== category && priorities[key] === priority)
      priorities[category] = priority
      if (conflict) {
        priorities[conflict] = previous || 'tertiary'
      }
      this.resetAttributeCategory(category)
      if (conflict) {
        this.resetAttributeCategory(conflict)
      }
      this.notifyPriorityReset('attributes')
    },
    setAbilityPriority(group, priority) {
      this.ensureCreationStructure()
      if (this.isCreationLocked) {
        this.notifyCreationLocked('abilities')
        return
      }
      if (!priority) {
        return
      }
      const priorities = this.form.creation.abilityPriorities
      if (!priorities || priorities[group] === priority) {
        return
      }
      const previous = priorities[group]
      const conflict = Object.keys(priorities).find((key) => key !== group && priorities[key] === priority)
      priorities[group] = priority
      if (conflict) {
        priorities[conflict] = previous || 'tertiary'
      }
      this.resetAbilityGroup(group)
      if (conflict) {
        this.resetAbilityGroup(conflict)
      }
      this.notifyPriorityReset('abilities')
    },
    handleGenerationChange() {
      this.ensureCreationStructure()
      const stats = this.currentGenerationStats
      if (!stats) {
        return
      }
      if (!this.form.bloodPool || typeof this.form.bloodPool !== 'object') {
        this.form.bloodPool = {
          max: stats.bloodPool?.max || 10,
          perTurn: stats.bloodPool?.perTurn || 1,
          current: 0
        }
      }
      this.form.bloodPool.max = stats.bloodPool?.max || this.form.bloodPool.max
      this.form.bloodPool.perTurn = stats.bloodPool?.perTurn || this.form.bloodPool.perTurn || 1
      if (this.form.bloodPool.current > this.form.bloodPool.max) {
        this.form.bloodPool.current = this.form.bloodPool.max
      }
      const attributeMax = stats.attributeMax || 5
      Object.keys(this.form.attributes || {}).forEach((categoryKey) => {
        const group = this.form.attributes[categoryKey] || {}
        Object.keys(group).forEach((attrKey) => {
          if (group[attrKey] > attributeMax) {
            group[attrKey] = attributeMax
          }
        })
      })
      const disciplineMax = stats.disciplineMax || 5
      ;(this.form.disciplines || []).forEach((discipline) => {
        if (discipline.rating > disciplineMax) {
          discipline.rating = disciplineMax
        }
      })
      this.cleanupAllBonuses()
      this.syncDerivedValues()
      this.syncGenerationBackgroundFromGeneration()
    },
    setAttributeRating(category, attribute, value) {
      this.ensureCreationStructure()
      const attributes = this.form.attributes?.[category]
      if (!attributes) {
        return
      }
      const current = Number(attributes[attribute]) || BASE_ATTRIBUTE_RATING
      const max = this.currentGenerationStats?.attributeMax || 5
      const desired = Math.max(BASE_ATTRIBUTE_RATING, Math.min(max, Number(value) || BASE_ATTRIBUTE_RATING))
      if (this.isExperienceMode) {
        const increase = desired - current
        if (desired === current) {
          return
        }
        if (increase < 0) {
          const baseline = this.getAttributeBaseline(category, attribute)
          if (desired < baseline) {
            this.setAllocationMessage(
              'attributes',
              'Os pontos definitivos não podem ser reduzidos. Utilize XP apenas para novos aumentos.'
            )
            return
          }
          const refundCost = this.calculateAttributeExperienceCost(desired, current)
          attributes[attribute] = desired
          const refunded = this.refundExperience(refundCost)
          this.setAllocationMessage(
            'attributes',
            refunded > 0
              ? `Ajuste revertido. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
              : 'Nenhum XP foi recuperado porque os pontos já estavam consolidados.'
          )
          return
        }
        if (increase > 0 && this.availableBonusPoints > 0 && this.canAllocateAttributePoints(category, increase)) {
          attributes[attribute] = desired
          this.cleanupAttributeBonus(category)
          this.setAllocationMessage(
            'attributes',
            `Pontos bônus aplicados. Bônus restantes: ${this.availableBonusPoints}.`
          )
          return
        }
        const cost = this.calculateAttributeExperienceCost(current, desired)
        if (!this.canSpendExperience(cost)) {
          this.setAllocationMessage(
            'attributes',
            `Experiência insuficiente. São necessários ${cost} XP disponíveis.`
          )
          return
        }
        this.spendExperience(cost)
        attributes[attribute] = desired
        this.setAllocationMessage(
          'attributes',
          `Aprimoramento concluído. ${cost} XP gastos. Restante: ${this.availableExperience} XP.`
        )
        return
      }
      if (this.isCreationLocked) {
        this.notifyCreationLocked('attributes')
        return
      }
      let target = desired
      if (current === desired && desired > BASE_ATTRIBUTE_RATING) {
        target = desired - 1
      }
      const newValue = Math.max(BASE_ATTRIBUTE_RATING, Math.min(max, target))
      const delta = newValue - current
      if (!delta) {
        return
      }
      if (delta > 0 && !this.canAllocateAttributePoints(category, delta)) {
        this.setAllocationMessage('attributes', 'Pontos insuficientes nesta categoria. Ajuste prioridades ou utilize bônus.')
        return
      }
      attributes[attribute] = newValue
      this.cleanupAttributeBonus(category)
      this.setAllocationMessage('attributes', '')
    },
    setAbilityRating(category, ability, value) {
      this.ensureCreationStructure()
      const abilities = this.form.abilities?.[category]
      if (!abilities) {
        return
      }
      const current = Number(abilities[ability]) || 0
      const desired = Math.max(0, Math.min(5, Number(value) || 0))
      if (this.isExperienceMode) {
        const increase = desired - current
        if (desired === current) {
          return
        }
        if (increase < 0) {
          const baseline = this.getAbilityBaseline(category, ability)
          if (desired < baseline) {
            this.setAllocationMessage(
              'abilities',
              'Os pontos definitivos não podem ser reduzidos. Utilize XP apenas para novos aumentos.'
            )
            return
          }
          const refundCost = this.calculateAbilityExperienceCost(desired, current)
          abilities[ability] = desired
          const refunded = this.refundExperience(refundCost)
          this.setAllocationMessage(
            'abilities',
            refunded > 0
              ? `Ajuste revertido. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
              : 'Nenhum XP foi recuperado porque os pontos já estavam consolidados.'
          )
          return
        }
        if (increase > 0 && this.availableBonusPoints > 0 && this.canAllocateAbilityPoints(category, increase)) {
          abilities[ability] = desired
          this.cleanupAbilityBonus(category)
          this.setAllocationMessage(
            'abilities',
            `Pontos bônus aplicados. Bônus restantes: ${this.availableBonusPoints}.`
          )
          return
        }
        const cost = this.calculateAbilityExperienceCost(current, desired)
        if (!this.canSpendExperience(cost)) {
          this.setAllocationMessage(
            'abilities',
            `Experiência insuficiente. São necessários ${cost} XP disponíveis.`
          )
          return
        }
        this.spendExperience(cost)
        abilities[ability] = desired
        this.setAllocationMessage(
          'abilities',
          `Aprimoramento concluído. ${cost} XP gastos. Restante: ${this.availableExperience} XP.`
        )
        return
      }
      if (this.isCreationLocked) {
        this.notifyCreationLocked('abilities')
        return
      }
      let target = desired
      if (current === desired && desired > 0) {
        target = desired - 1
      }
      const newValue = Math.max(0, Math.min(5, target))
      const delta = newValue - current
      if (!delta) {
        return
      }
      if (delta > 0 && !this.canAllocateAbilityPoints(category, delta)) {
        this.setAllocationMessage('abilities', 'Pontos insuficientes nesta categoria. Ajuste prioridades ou utilize bônus.')
        return
      }
      abilities[ability] = newValue
      this.cleanupAbilityBonus(category)
      this.setAllocationMessage('abilities', '')
    },
    setDisciplineRating(index, value) {
      this.ensureCreationStructure()
      if (!this.form.disciplines[index]) {
        return
      }
      const discipline = this.form.disciplines[index]
      const current = Number(discipline.rating) || 0
      const max = this.currentGenerationStats?.disciplineMax || 5
      const desired = Math.max(0, Math.min(max, Number(value) || 0))
      if (this.isExperienceMode) {
        const increase = desired - current
        if (desired === current) {
          return
        }
        if (increase < 0) {
          const baseline = this.getDisciplineBaseline(discipline.name)
          if (desired < baseline) {
            this.setAllocationMessage(
              'disciplines',
              'Os pontos definitivos não podem ser reduzidos. Utilize XP apenas para novos aumentos.'
            )
            return
          }
          const refundCost = this.calculateDisciplineExperienceCost(desired, current)
          discipline.rating = desired
          const refunded = this.refundExperience(refundCost)
          this.setAllocationMessage(
            'disciplines',
            refunded > 0
              ? `Ajuste revertido. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
              : 'Nenhum XP foi recuperado porque os pontos já estavam consolidados.'
          )
          return
        }
        if (increase > 0 && this.availableBonusPoints > 0 && this.canAllocateDisciplinePoints(increase)) {
          discipline.rating = desired
          this.cleanupDisciplineBonus()
          this.setAllocationMessage(
            'disciplines',
            `Pontos bônus aplicados. Bônus restantes: ${this.availableBonusPoints}.`
          )
          return
        }
        const cost = this.calculateDisciplineExperienceCost(current, desired)
        if (!this.canSpendExperience(cost)) {
          this.setAllocationMessage(
            'disciplines',
            `Experiência insuficiente. São necessários ${cost} XP disponíveis.`
          )
          return
        }
        this.spendExperience(cost)
        discipline.rating = desired
        this.setAllocationMessage(
          'disciplines',
          `Aprimoramento concluído. ${cost} XP gastos. Restante: ${this.availableExperience} XP.`
        )
        return
      }
      if (this.isCreationLocked) {
        this.notifyCreationLocked('disciplines')
        return
      }
      let target = desired
      if (current === desired && desired > 0) {
        target = desired - 1
      }
      const newValue = Math.max(0, Math.min(max, target))
      const delta = newValue - current
      if (!delta) {
        return
      }
      if (delta > 0 && !this.canAllocateDisciplinePoints(delta)) {
        this.setAllocationMessage('disciplines', 'Você não possui pontos de disciplina disponíveis.')
        return
      }
      discipline.rating = newValue
      this.cleanupDisciplineBonus()
      this.setAllocationMessage('disciplines', '')
    },
    setVirtueRating(key, value) {
      if (this.isCreationLocked) {
        this.notifyCreationLocked('virtues')
        return
      }
      const virtues = this.form.virtues || {}
      const current = Number(virtues[key]) || BASE_VIRTUE_RATING
      let target = value
      if (current === value && value > BASE_VIRTUE_RATING) {
        target = value - 1
      }
      const newValue = Math.max(BASE_VIRTUE_RATING, Math.min(5, target))
      const delta = newValue - current
      if (!delta) {
        return
      }
      if (delta > 0 && this.virtuePointsSpent + delta > 7) {
        this.setAllocationMessage('virtues', 'Distribua no máximo 7 pontos de virtudes.')
        return
      }
      virtues[key] = newValue
      this.syncDerivedValues()
      this.setAllocationMessage('virtues', '')
    },
    setBackgroundRating(index, value) {
      this.ensureCreationStructure()
      const entry = this.form.backgrounds?.[index]
      if (!entry) {
        return
      }
      const isGeneration = this.isGenerationBackground(entry)
      const current = Number(entry.rating) || 0
      const desired = Math.max(0, Math.min(5, Number(value) || 0))
      if (this.isExperienceMode) {
        const increase = desired - current
        if (desired === current) {
          return
        }
        if (increase < 0) {
          const baseline = this.getBackgroundBaseline(entry.name)
          if (desired < baseline) {
            this.setAllocationMessage(
              'backgrounds',
              'Os pontos definitivos não podem ser reduzidos. Utilize XP apenas para novos aumentos.'
            )
            return
          }
          const refundCost = this.calculateBackgroundExperienceCost(desired, current)
          entry.rating = desired
          if (isGeneration) {
            this.syncGenerationBackgroundFromRating()
            this.handleGenerationChange()
          }
          const refunded = this.refundExperience(refundCost)
          this.setAllocationMessage(
            'backgrounds',
            refunded > 0
              ? `Ajuste revertido. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
              : 'Nenhum XP foi recuperado porque os pontos já estavam consolidados.'
          )
          return
        }
        if (increase > 0 && this.availableBonusPoints > 0 && this.canAllocateBackgroundPoints(increase)) {
          entry.rating = desired
          if (isGeneration) {
            this.syncGenerationBackgroundFromRating()
            this.handleGenerationChange()
          }
          this.cleanupBackgroundBonus()
          this.setAllocationMessage(
            'backgrounds',
            `Pontos bônus aplicados. Bônus restantes: ${this.availableBonusPoints}.`
          )
          return
        }
        const cost = this.calculateBackgroundExperienceCost(current, desired)
        if (!this.canSpendExperience(cost)) {
          this.setAllocationMessage(
            'backgrounds',
            `Experiência insuficiente. São necessários ${cost} XP disponíveis.`
          )
          return
        }
        this.spendExperience(cost)
        entry.rating = desired
        if (isGeneration) {
          this.syncGenerationBackgroundFromRating()
          this.handleGenerationChange()
        }
        this.setAllocationMessage(
          'backgrounds',
          `Aprimoramento concluído. ${cost} XP gastos. Restante: ${this.availableExperience} XP.`
        )
        return
      }
      if (this.isCreationLocked) {
        this.notifyCreationLocked('backgrounds')
        return
      }
      let target = desired
      if (current === desired && desired > 0) {
        target = desired - 1
      }
      const newValue = Math.max(0, Math.min(5, target))
      const delta = newValue - current
      if (!delta) {
        return
      }
      if (delta > 0 && !this.canAllocateBackgroundPoints(delta)) {
        this.setAllocationMessage('backgrounds', 'Você não possui pontos de antecedentes disponíveis.')
        return
      }
      entry.rating = newValue
      if (isGeneration) {
        this.syncGenerationBackgroundFromRating()
      }
      this.cleanupBackgroundBonus()
      this.setAllocationMessage('backgrounds', '')
      if (isGeneration) {
        this.handleGenerationChange()
      }
    },
    addBackground() {
      this.ensureCreationStructure()
      if (this.isCreationLocked && !this.isExperienceMode) {
        this.notifyCreationLocked('backgrounds')
        return
      }
      const name = (this.newBackgroundName || '').trim()
      if (!name) {
        return
      }
      const normalizedName = this.normalizeBackgroundName(name)
      const exists = (this.form.backgrounds || []).some(
        (background) => this.normalizeBackgroundName(background.name) === normalizedName
      )
      if (!exists) {
        if (!Array.isArray(this.form.backgrounds)) {
          this.form.backgrounds = []
        }
        const newEntry = { name, rating: 0 }
        this.form.backgrounds.push(newEntry)
        if (this.isGenerationBackground(newEntry)) {
          this.syncGenerationBackgroundFromRating()
          this.handleGenerationChange()
        }
      }
      this.newBackgroundName = ''
    },
    removeBackground(index) {
      if (!Array.isArray(this.form.backgrounds)) {
        return
      }
      const entry = this.form.backgrounds[index]
      if (!entry) {
        return
      }
      if (this.isExperienceMode) {
        const baseline = this.getBackgroundBaseline(entry.name)
        if (baseline > 0) {
          this.setAllocationMessage(
            'backgrounds',
            'Antecedentes definitivos não podem ser removidos após o salvamento. Utilize XP apenas para novos aumentos.'
          )
          return
        }
      } else if (this.isCreationLocked) {
        this.notifyCreationLocked('backgrounds')
        return
      }
      const refundCost = this.isExperienceMode
        ? this.calculateBackgroundExperienceCost(0, Number(entry.rating) || 0)
        : 0
      const refunded = this.isExperienceMode ? this.refundExperience(refundCost) : 0
      const [removed] = this.form.backgrounds.splice(index, 1)
      this.cleanupBackgroundBonus()
      if (this.isGenerationBackground(removed)) {
        this.form.generation = 13
        this.handleGenerationChange()
      }
      if (this.isExperienceMode) {
        if (refunded > 0) {
          this.setAllocationMessage(
            'backgrounds',
            `Remoção concluída. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
          )
        } else {
          this.setAllocationMessage('backgrounds', '')
        }
      }
    },
    setTrackCurrent(trackKey, value) {
      const track = this.form[trackKey]
      if (!track) {
        return
      }
      const max = Math.max(0, Number(track.max) || 0)
      const current = Number(track.current) || 0
      const nextValue = current === value ? value - 1 : value
      track.current = Math.max(0, Math.min(max, nextValue))
    },
    canRemoveDiscipline(name) {
      return !this.clanDisciplineNames.has(String(name).toLowerCase())
    },
    addDiscipline() {
      const name = (this.newDisciplineName || '').trim()
      if (this.isCreationLocked && !this.isExperienceMode) {
        this.notifyCreationLocked('disciplines')
        return
      }
      if (!name) {
        return
      }
      const exists = this.form.disciplines.some(
        (discipline) => String(discipline.name).toLowerCase() === name.toLowerCase()
      )
      if (!exists) {
        this.form.disciplines.push({ name, rating: 0 })
      }
      this.newDisciplineName = ''
    },
    removeDiscipline(index) {
      if (!Array.isArray(this.form.disciplines)) {
        return
      }
      const discipline = this.form.disciplines[index]
      if (!discipline) {
        return
      }
      if (!this.canRemoveDiscipline(discipline.name)) {
        return
      }
      if (this.isExperienceMode) {
        const baseline = this.getDisciplineBaseline(discipline.name)
        if (baseline > 0) {
          this.setAllocationMessage(
            'disciplines',
            'Disciplinas definitivas não podem ser removidas após o salvamento. Utilize XP apenas para novos aumentos.'
          )
          return
        }
      } else if (this.isCreationLocked) {
        this.notifyCreationLocked('disciplines')
        return
      }
      const refundCost = this.isExperienceMode
        ? this.calculateDisciplineExperienceCost(0, Number(discipline.rating) || 0)
        : 0
      const refunded = this.isExperienceMode ? this.refundExperience(refundCost) : 0
      this.form.disciplines.splice(index, 1)
      this.cleanupDisciplineBonus()
      if (this.isExperienceMode) {
        if (refunded > 0) {
          this.setAllocationMessage(
            'disciplines',
            `Remoção concluída. ${refunded} XP devolvidos. Restante: ${this.availableExperience} XP.`
          )
        } else {
          this.setAllocationMessage('disciplines', '')
        }
      }
    },
    applyClanDisciplines(clanId) {
      const clan = this.availableClans.find((item) => item.id === clanId)
      const existing = new Map(
        this.form.disciplines.map((discipline) => [String(discipline.name).toLowerCase(), discipline.rating])
      )
      const mapped = (clan?.disciplines || []).map((name) => ({
        name,
        rating: Math.min(existing.get(String(name).toLowerCase()) || 0, this.currentGenerationStats?.disciplineMax || 5)
      }))
      const extras = this.form.disciplines.filter(
        (discipline) => !mapped.some((item) => item.name === discipline.name)
      )
      this.form.disciplines = [...mapped, ...extras]
      this.cleanupDisciplineBonus()
    },
    selectSheet(sheet) {
      this.isHydratingForm = true
      this.selectedSheetId = sheet.id
      this.resetExperienceBaselines()
      const defaults = createEmptySheet()
      const normalizedBackgrounds = Array.isArray(sheet.backgrounds)
        ? sheet.backgrounds
            .map((item) => {
              if (!item) {
                return null
              }
              if (typeof item === 'string') {
                return { name: item, rating: 0 }
              }
              const name = item.name || ''
              if (!name) {
                return null
              }
              const rating = Math.max(0, Math.min(5, Number(item.rating) || 0))
              return { name, rating }
            })
            .filter(Boolean)
        : []
      const parsedGeneration = (() => {
        const raw = sheet.generation
        if (raw === null || raw === undefined || raw === '') {
          return defaults.generation
        }
        if (Number.isFinite(Number(raw))) {
          return Number(raw)
        }
        const match = String(raw).match(/\d+/)
        return match ? Number(match[0]) : defaults.generation
      })()
      const generationStats =
        generationOptions.find((option) => option.value === parsedGeneration) || generationOptions[0]
      const defaultsCreation = JSON.parse(JSON.stringify(defaults.creation))
      const incomingCreation = sheet.creation || {}
      const mergedCreation = {
        attributePriorities: {
          ...defaultsCreation.attributePriorities,
          ...(incomingCreation.attributePriorities || {})
        },
        abilityPriorities: {
          ...defaultsCreation.abilityPriorities,
          ...(incomingCreation.abilityPriorities || {})
        },
        bonusAllocations: {
          attributes: {
            ...defaultsCreation.bonusAllocations.attributes,
            ...(incomingCreation.bonusAllocations?.attributes || {})
          },
          abilities: {
            ...defaultsCreation.bonusAllocations.abilities,
            ...(incomingCreation.bonusAllocations?.abilities || {})
          },
          disciplines:
            incomingCreation.bonusAllocations?.disciplines ?? defaultsCreation.bonusAllocations.disciplines,
          backgrounds:
            incomingCreation.bonusAllocations?.backgrounds ?? defaultsCreation.bonusAllocations.backgrounds
        }
      }
      this.form = JSON.parse(JSON.stringify({
        ...defaults,
        ...sheet,
        generation: parsedGeneration,
        attributes: sheet.attributes || defaults.attributes,
        abilities: sheet.abilities || defaults.abilities,
        virtues: sheet.virtues || defaults.virtues,
        experience: sheet.experience || defaults.experience,
        health: sheet.health || defaults.health,
        willpower: sheet.willpower || defaults.willpower,
        bloodPool: sheet.bloodPool || defaults.bloodPool,
        backgrounds: normalizedBackgrounds,
        creation: mergedCreation
      }))
      this.form.status = String(this.form.status || 'draft').toLowerCase()
      this.originalStatus = this.form.status
      Object.keys(this.form.virtues || {}).forEach((key) => {
        this.form.virtues[key] = Math.max(
          BASE_VIRTUE_RATING,
          Math.min(5, Number(this.form.virtues[key]) || BASE_VIRTUE_RATING)
        )
      })
      const attributeCap = generationStats.attributeMax || 5
      Object.keys(this.form.attributes || {}).forEach((categoryKey) => {
        const group = this.form.attributes[categoryKey]
        Object.keys(group).forEach((attrKey) => {
          group[attrKey] = Math.max(
            BASE_ATTRIBUTE_RATING,
            Math.min(attributeCap, Number(group[attrKey]) || BASE_ATTRIBUTE_RATING)
          )
        })
      })
      const disciplineCap = generationStats.disciplineMax || 5
      this.form.disciplines = (Array.isArray(this.form.disciplines) ? this.form.disciplines : []).map((discipline) => ({
        name: discipline.name,
        rating: Math.max(0, Math.min(disciplineCap, Number(discipline.rating) || 0))
      }))
      this.selectedQualities = this.normalizeSelection(sheet.qualities, this.qualityOptions)
      this.lastValidQualitySelection = [...this.selectedQualities]
      this.selectedDefects = this.normalizeSelection(sheet.defects, this.defectOptions)
      this.lastValidDefectSelection = [...this.selectedDefects]
      this.meritsText = formatList(sheet.merits)
      this.flawsText = formatList(sheet.flaws)
      this.newDisciplineName = ''
      this.newBackgroundName = ''
      if (this.defectPointsTotal > this.maxDefectPoints) {
        let total = 0
        const limited = []
        this.selectedDefects.forEach((id) => {
          const option = this.defectOptions.find((item) => item.id === id)
          if (!option) {
            return
          }
          if (total + option.points <= this.maxDefectPoints) {
            total += option.points
            limited.push(id)
          }
        })
        this.selectedDefects = limited
        this.lastValidDefectSelection = [...limited]
      }
      if (this.qualityCostTotal > this.defectPointsTotal) {
        this.selectedQualities = []
        this.lastValidQualitySelection = []
      }
      this.ensureCreationStructure()
      this.cleanupAllBonuses()
      this.handleGenerationChange()
      this.isHydratingForm = false
      if (this.bonusBalance < 0) {
        this.setAllocationMessage('bonus', BONUS_DEFICIT_MESSAGE)
      } else {
        this.setAllocationMessage('bonus', '')
      }
      this.applyClanDisciplines(this.form.clan)
      this.captureExperienceBaselines()
      this.success = ''
      this.error = ''
    },
    resetForm() {
      this.selectedSheetId = null
      this.form = createEmptySheet()
      this.resetExperienceBaselines()
      this.originalStatus = 'draft'
      this.meritsText = ''
      this.flawsText = ''
      this.newDisciplineName = ''
      this.newBackgroundName = ''
      this.selectedQualities = []
      this.selectedDefects = []
      this.lastValidQualitySelection = []
      this.lastValidDefectSelection = []
      this.allocationMessages = {
        attributes: '',
        abilities: '',
        disciplines: '',
        backgrounds: '',
        virtues: '',
        bonus: ''
      }
      Object.keys(this.allocationMessageTimers).forEach((key) => clearTimeout(this.allocationMessageTimers[key]))
      this.allocationMessageTimers = {}
      this.success = ''
      this.error = ''
      this.isHydratingForm = false
      this.handleGenerationChange()
    },
    printSheet() {
      this.$nextTick(() => {
        window.print()
      })
    },
    confirmDelete(sheet) {
      if (!sheet || !sheet.id) {
        return
      }
      if (window.confirm(`Excluir a ficha "${sheet.name}"? Esta ação não pode ser desfeita.`)) {
        this.deleteSheet(sheet.id)
      }
    },
    confirmDeleteById(id) {
      const sheet = this.sheets.find((item) => item.id === id)
      if (sheet) {
        this.confirmDelete(sheet)
      }
    },
    async deleteSheet(id) {
      if (!this.user?.id) {
        this.$emit('login-required')
        return
      }
      this.saving = true
      this.error = ''
      try {
        await deleteCharacterSheet(id, this.user.id)
        this.success = 'Ficha removida com sucesso.'
        if (this.selectedSheetId === id) {
          this.resetForm()
        }
        await this.loadSheets()
      } catch (error) {
        this.error = error.response?.data?.error || 'Não foi possível remover a ficha.'
      } finally {
        this.saving = false
      }
    },
    buildPayload() {
      return {
        userId: this.user.id,
        name: this.form.name,
        concept: this.form.concept,
        chronicle: this.form.chronicle,
        sect: this.form.sect,
        clan: this.form.clan,
        generation: this.form.generation,
        lineage: this.form.lineage,
        status: this.form.status,
        attributes: this.form.attributes,
        abilities: this.form.abilities,
        disciplines: this.form.disciplines,
        inventory: this.form.inventory,
        description: this.form.description,
        history: this.form.history,
        virtues: this.form.virtues,
        experience: (() => {
          const total = Math.max(0, Number(this.form.experience.total) || 0)
          const spent = Math.max(0, Math.min(total, Number(this.form.experience.spent) || 0))
          return { total, spent }
        })(),
        health: {
          max: HEALTH_LEVELS.length,
          current: Math.max(
            0,
            Math.min(HEALTH_LEVELS.length, Number(this.form.health?.current) || 0)
          )
        },
        willpower: {
          max: this.willpowerMax,
          current: Math.max(
            0,
            Math.min(this.willpowerMax, Number(this.form.willpower?.current) || 0)
          )
        },
        bloodPool: {
          max: this.bloodPoolMax,
          perTurn: this.bloodPerTurn,
          current: Math.max(
            0,
            Math.min(this.bloodPoolMax, Number(this.form.bloodPool?.current) || 0)
          )
        },
        notes: this.form.notes,
        qualities: this.selectedQualityDetails,
        defects: this.selectedDefectDetails,
        merits: parseList(this.meritsText),
        flaws: parseList(this.flawsText),
        backgrounds: (this.form.backgrounds || []).map((background) => ({
          name: background.name,
          rating: Math.max(0, Math.min(5, Number(background.rating) || 0))
        })),
        creation: this.form.creation
      }
    },
    validateForm() {
      if (!this.form.name) {
        this.error = 'Informe o nome do personagem.'
        return false
      }
      if (!this.form.sect) {
        this.error = 'Selecione a seita do personagem.'
        return false
      }
      if (!this.form.clan) {
        this.error = 'Selecione o clã do personagem.'
        return false
      }
      return true
    },
    async submit() {
      if (!this.user?.id) {
        this.$emit('login-required')
        return
      }
      if (!this.validateForm()) {
        return
      }
      this.form.status = String(this.form.status || 'draft').toLowerCase()
      if (this.originalStatus === 'final' && this.form.status !== 'final') {
        this.error = 'Esta ficha já está definitiva e não pode voltar para rascunho.'
        return
      }
      const isFinalizing = this.form.status === 'final' && this.originalStatus !== 'final'
      if (isFinalizing) {
        const confirmed = window.confirm(
          'Marcar a ficha como definitiva bloqueia a redistribuição de pontos iniciais. Deseja continuar?'
        )
        if (!confirmed) {
          return
        }
      }
      if (!this.selectedSheetId && this.isAtLimit) {
        this.error = 'Limite de fichas atingido para o seu plano. Exclua uma ficha ou torne-se usuário premium.'
        return
      }

      this.saving = true
      this.error = ''
      this.success = ''

      const payload = this.buildPayload()

      try {
        if (this.selectedSheetId) {
          await updateCharacterSheet(this.selectedSheetId, payload)
          this.success = 'Ficha atualizada com sucesso.'
          this.originalStatus = this.form.status
          this.captureExperienceBaselines()
        } else {
          await createCharacterSheet(payload)
          this.success = 'Ficha criada com sucesso.'
          this.resetForm()
        }
        await this.loadSheets()
      } catch (error) {
        this.error = error.response?.data?.error || 'Não foi possível salvar a ficha de personagem.'
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.character-sheet-wrapper {
  background: rgba(10, 10, 10, 0.8);
  min-height: calc(100vh - 80px);
  color: #f8f9fa;
}

.sheet-card {
  background: rgba(26, 26, 26, 0.92);
  border: 1px solid rgba(218, 165, 32, 0.25);
  color: #f8f9fa;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.4);
}

.sheet-card .form-control,
.sheet-card .form-select,
.sheet-card textarea {
  background-color: rgba(33, 33, 33, 0.85);
  border-color: rgba(139, 0, 0, 0.55);
  color: #f8f9fa;
}

.sheet-card .form-control:focus,
.sheet-card .form-select:focus,
.sheet-card textarea:focus {
  border-color: var(--vampire-gold);
  box-shadow: 0 0 0 0.2rem rgba(218, 165, 32, 0.25);
}

.dot-rating {
  --dot-size: 20px;
  --dot-gap: 0.35rem;
  display: flex;
  flex-wrap: wrap;
  gap: var(--dot-gap);
  justify-content: flex-start;
  max-width: calc(var(--dot-size) * 10 + var(--dot-gap) * 9);
}

.dot-rating .dot {
  flex: 0 0 var(--dot-size);
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: 50%;
  border: 1px solid rgba(218, 165, 32, 0.6);
  background: transparent;
  padding: 0;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
}

.dot-rating .dot.active {
  background: var(--vampire-gold);
}

.dot-rating .dot:hover {
  transform: scale(1.1);
  background: rgba(218, 165, 32, 0.6);
}

.dot-rating--readonly {
  pointer-events: none;
}

.dot-rating--readonly .dot {
  cursor: default;
}

.dot-rating .dot--disabled {
  opacity: 0.35;
  cursor: default;
}

.dot-rating .dot--disabled:hover {
  transform: none;
  background: transparent;
}

.track-section {
  padding: 1rem;
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 0.75rem;
  background: rgba(18, 18, 18, 0.6);
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.track-section__label {
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(233, 236, 239, 0.75);
}

.health-track {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding: 0;
  margin: 0;
}

.health-track__item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.health-track__box {
  width: 22px;
  height: 22px;
  border-radius: 0.35rem;
  border: 1px solid rgba(218, 165, 32, 0.6);
  background: transparent;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
}

.health-track__box:hover {
  transform: scale(1.05);
  background: rgba(218, 165, 32, 0.4);
}

.health-track__box--filled {
  background: var(--vampire-gold);
  border-color: var(--vampire-gold);
}

.health-track__label {
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.health-track__label-title {
  font-weight: 600;
  letter-spacing: 0.03em;
}

.health-track__label-penalty {
  font-size: 0.85rem;
  color: rgba(233, 236, 239, 0.7);
}

.choice-grid {
  display: grid;
  gap: 0.75rem;
}

.choice-item {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.85rem 1rem;
  border: 1px solid rgba(218, 165, 32, 0.2);
  border-radius: 0.75rem;
  background: rgba(26, 26, 26, 0.65);
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;
  cursor: pointer;
}

.choice-item:hover {
  transform: translateY(-2px);
  border-color: rgba(218, 165, 32, 0.45);
}

.choice-item--active {
  border-color: rgba(218, 165, 32, 0.65);
  background: rgba(139, 0, 0, 0.2);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
}

.choice-item .form-check-input {
  margin-top: 0.4rem;
  cursor: pointer;
}

.choice-item-content {
  flex: 1;
}

.choice-item-title {
  font-weight: 600;
}

.choice-item-description {
  color: rgba(233, 236, 239, 0.75);
  font-size: 0.9rem;
}

.selection-panel {
  border: 1px solid rgba(218, 165, 32, 0.35);
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.75), rgba(18, 18, 18, 0.65));
  border-radius: 0.85rem;
  padding: 1rem 1.25rem;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  box-shadow: 0 12px 22px rgba(0, 0, 0, 0.35);
}

.selection-panel--alt {
  background: linear-gradient(145deg, rgba(34, 16, 16, 0.85), rgba(26, 12, 12, 0.75));
}

.selection-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.selection-panel__meta {
  font-size: 0.85rem;
  color: rgba(233, 236, 239, 0.8);
}

.bonus-summary {
  margin-top: 0.5rem;
  padding: 0.9rem 1.1rem;
  border-radius: 0.75rem;
  background: rgba(18, 18, 18, 0.6);
  border: 1px solid rgba(218, 165, 32, 0.35);
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem;
  color: rgba(233, 236, 239, 0.85);
  font-size: 0.9rem;
}

.bonus-summary span {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
}

.discipline-row {
  border: 1px solid rgba(218, 165, 32, 0.25);
  border-radius: 0.5rem;
  padding: 0.75rem;
  margin-bottom: 0.75rem;
  background: rgba(18, 18, 18, 0.6);
}

.btn-vampire {
  background: var(--vampire-red);
  color: #f8f9fa;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  padding: 0.75rem 2rem;
}

.btn-vampire:hover {
  background: var(--vampire-blood);
}

.list-group-item {
  border-color: rgba(218, 165, 32, 0.15);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.print-layout {
  display: none;
  margin: 0 auto;
}

.print-page {
  position: relative;
  min-height: 1200px;
  padding: 3rem;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: stretch;
}

.print-page--front {
  background-image: url('~@/assets/character-sheet-front.svg');
}

.print-page--back {
  background-image: url('~@/assets/character-sheet-back.svg');
}

.print-overlay {
  background: rgba(255, 255, 255, 0.82);
  border-radius: 1rem;
  padding: 2.5rem;
  border: 1px solid rgba(109, 90, 72, 0.35);
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.print-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  border-bottom: 1px solid rgba(109, 90, 72, 0.35);
  padding-bottom: 1rem;
}

.print-title {
  margin: 0;
  font-size: 1.75rem;
  color: #2f261c;
}

.print-subtitle {
  margin: 0.25rem 0 0;
  color: #6d5a48;
}

.print-meta-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.35rem 1rem;
  font-size: 0.95rem;
  color: #3b3126;
}

.print-section {
  background: rgba(247, 244, 238, 0.8);
  border: 1px solid rgba(109, 90, 72, 0.25);
  border-radius: 0.75rem;
  padding: 1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.print-section--inline {
  flex-direction: row;
  gap: 1.25rem;
}

.print-section--columns {
  flex-direction: row;
  gap: 1rem;
}

.print-section--summary {
  align-items: stretch;
}

.print-section--notes {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
}

.print-section__title {
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #3b3126;
}

.print-box {
  background: rgba(255, 255, 255, 0.85);
  border: 1px solid rgba(109, 90, 72, 0.4);
  border-radius: 0.75rem;
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

.print-box--alt {
  background: rgba(255, 248, 241, 0.9);
}

.print-box__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid rgba(109, 90, 72, 0.2);
}

.print-box__list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.print-box__item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.4rem 0;
  border-bottom: 1px dashed rgba(109, 90, 72, 0.35);
}

.print-box__item:last-child {
  border-bottom: none;
}

.print-box__content {
  flex: 1;
}

.print-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.print-column {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.print-column__title {
  margin: 0 0 0.25rem;
  color: #5a4a3d;
  font-size: 0.95rem;
}

.print-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.print-row__label {
  color: #2f261c;
}

.print-dot-row {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 14px;
  gap: 0.2rem;
  justify-content: flex-end;
}

.print-dots {
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 16px;
  gap: 0.25rem;
}

.print-dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1px solid rgba(109, 90, 72, 0.5);
  background: transparent;
}

.print-dot--filled {
  background: #2f261c;
  border-color: #2f261c;
}

.print-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.print-list__item {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  padding: 0.65rem 0.75rem;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(109, 90, 72, 0.2);
  border-radius: 0.5rem;
}

.print-text {
  margin: 0;
  color: #3b3126;
  white-space: pre-line;
}

.print-empty {
  color: #6d5a48;
  margin: 0;
}

.print-chip {
  background: #9c8267;
  color: #fff;
  padding: 0.15rem 0.55rem;
  border-radius: 999px;
  font-size: 0.85rem;
}

.print-chip--positive {
  background: #48734d;
}

.print-stat-card {
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(109, 90, 72, 0.25);
  border-radius: 0.6rem;
  padding: 0.85rem;
  align-self: stretch;
}

.selection-panel--print {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(109, 90, 72, 0.4);
  box-shadow: none;
}

@media print {
  :global(body) {
    background: #fff !important;
  }

  .character-sheet-wrapper {
    background: transparent;
    padding: 0;
  }

  :global(.navbar) {
    display: none !important;
  }

  .character-sheet-wrapper > .container {
    display: none !important;
  }

  .print-layout {
    display: block !important;
  }

  .print-page {
    display: block;
    page-break-after: always;
    min-height: auto;
  }

  .print-overlay {
    background: transparent;
    border: none;
  }
}

@media (max-width: 992px) {
  .dot-rating {
    --dot-size: 18px;
    --dot-gap: 0.25rem;
  }

  .choice-grid {
    gap: 0.6rem;
  }
}
</style>
